import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import styled, { createGlobalStyle, keyframes } from "styled-components";

const GlobalFonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
`;

function WhatsAppIcon({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg: "#f8fafc",
  card: "#ffffff",
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  primaryLight: "#eef2ff",
  danger: "#ef4444",
  dangerLight: "#fef2f2",
  warn: "#f59e0b",
  warnLight: "#fffbeb",
  success: "#10b981",
  successLight: "#ecfdf5",
  text: "#0f172a",
  textSec: "#475569",
  muted: "#94a3b8",
  border: "#e2e8f0",
  borderHover: "#cbd5e1",
  waGreen: "#25D366",
  waGreenDark: "#128C7E",
  waBubbleIn: "#f1f5f9",
  waBubbleOut: "#ede9fe",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -1px rgba(0,0,0,0.04)",
  shadowLg: "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.04)",
};

const fadeIn = keyframes`from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const NotifCard = styled.div`
  color: white; border-radius: 14px; padding: 14px 18px;
  display: flex; align-items: center; gap: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  max-width: 380px; cursor: pointer; pointer-events: all;
  animation: ${slideUp} 0.25s ease;
  background: ${p => p.$tipo === "success" ? "#1e293b" : C.danger};
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Wrap = styled.div`
  font-family: 'Inter', 'Segoe UI', sans-serif;
  color: ${C.text};
  min-height: 100%;
  background: ${C.bg};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${C.text};
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 28px;
  background: ${C.card};
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 4px;
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: ${C.shadow};
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  color: ${({ active }) => (active ? C.primary : C.textSec)};
  background: ${({ active }) => (active ? C.primaryLight : "transparent")};
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover {
    background: ${({ active }) => (active ? C.primaryLight : C.bg)};
    color: ${({ active }) => (active ? C.primary : C.text)};
  }
`;

const Badge = styled.span`
  background: ${({ variant }) => variant === "accent" ? C.primary : C.danger};
  color: white;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  min-width: 18px;
  text-align: center;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const Btn = styled.button`
  padding: ${({ sm }) => (sm ? "6px 12px" : "9px 18px")};
  font-size: ${({ sm }) => (sm ? "13px" : "14px")};
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
  white-space: nowrap;
  background: ${({ variant }) =>
    variant === "danger"  ? C.danger :
    variant === "wa"      ? C.waGreen :
    variant === "ghost"   ? "transparent" :
    variant === "outline" ? "transparent" :
    C.primary};
  color: ${({ variant }) =>
    variant === "ghost"   ? C.textSec :
    variant === "outline" ? C.primary :
    "white"};
  border: ${({ variant }) =>
    variant === "outline" ? `1.5px solid ${C.primary}` :
    variant === "ghost"   ? `1px solid ${C.border}` :
    "none"};
  box-shadow: ${({ variant }) =>
    variant === "ghost" || variant === "outline" ? "none" : C.shadow};
  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
    box-shadow: ${({ variant }) =>
      variant === "ghost" || variant === "outline" ? "none" : C.shadowMd};
  }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// ─── Card ─────────────────────────────────────────────────────────────────────
const Card = styled.div`
  background: ${C.card};
  border: 1px solid ${C.border};
  border-radius: 12px;
  box-shadow: ${C.shadow};
  overflow: hidden;
  animation: ${fadeIn} 0.2s ease;
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: ${C.bg};
  border-bottom: 1px solid ${C.border};
  font-weight: 600;
  font-size: 12px;
  color: ${C.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 13px 16px;
  border-bottom: 1px solid ${C.border};
  vertical-align: middle;
  color: ${C.text};
`;

const Tr = styled.tr`
  transition: background 0.1s;
  &:hover td { background: #fafbfc; }
  &:last-child td { border-bottom: none; }
`;

// ─── Badges ───────────────────────────────────────────────────────────────────
const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ color }) => color || C.primaryLight};
  color: ${({ textColor }) => textColor || C.primary};
`;

// ─── Form ─────────────────────────────────────────────────────────────────────
const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: ${C.text};
  background: white;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: ${C.primary}; box-shadow: 0 0 0 3px ${C.primaryLight}; }
  &::placeholder { color: ${C.muted}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: ${C.text};
  background: white;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: ${C.primary}; box-shadow: 0 0 0 3px ${C.primaryLight}; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: ${C.text};
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: ${C.primary}; box-shadow: 0 0 0 3px ${C.primaryLight}; }
  &::placeholder { color: ${C.muted}; }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${C.textSec};
  display: block;
  margin-bottom: 6px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FullCol = styled.div`grid-column: 1 / -1;`;

// ─── Modal ────────────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(2px);
  z-index: 2000;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${C.shadowLg};
  animation: ${fadeIn} 0.2s ease;
  @media (max-width: 600px) { padding: 20px 16px; border-radius: 12px; }
`;

const ModalTitle = styled.h3`
  margin: 0 0 24px;
  font-size: 18px;
  font-weight: 700;
  color: ${C.text};
  letter-spacing: -0.2px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${C.border};
  margin: 20px 0;
`;

// ─── Search ───────────────────────────────────────────────────────────────────
const SearchWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  @media (max-width: 700px) { width: 100%; }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 11px;
  color: ${C.muted};
  font-size: 14px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  padding: 8px 12px 8px 32px;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  width: 240px;
  background: white;
  color: ${C.text};
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: ${C.primary}; box-shadow: 0 0 0 3px ${C.primaryLight}; }
  &::placeholder { color: ${C.muted}; }
  @media (max-width: 700px) { width: 100%; }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
  @media (max-width: 700px) { flex-direction: column; align-items: stretch; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 0;
  color: ${C.muted};
  font-size: 15px;
`;

const SelectionBar = styled.div`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: ${C.text};
  color: white;
  border-radius: 14px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.22);
  z-index: 1000;
  animation: ${slideUp} 0.2s ease;
  white-space: nowrap;
  @media (max-width: 700px) {
    left: 12px;
    right: 12px;
    transform: none;
    bottom: 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const ProgressBar = styled.div`
  height: 6px;
  background: ${C.border};
  border-radius: 99px;
  overflow: hidden;
  margin: 12px 0 4px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${C.waGreen};
  border-radius: 99px;
  transition: width 0.3s ease;
  width: ${({ pct }) => pct}%;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${C.primary};
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 700px) { display: block; }
`;

const DesktopOnly = styled.div`
  display: block;
  @media (max-width: 700px) { display: none; }
`;

// ─── Chat ─────────────────────────────────────────────────────────────────────
const ChatWrap = styled.div`
  display: flex;
  height: calc(100vh - 220px);
  min-height: 440px;
  border: 1px solid ${C.border};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${C.shadow};
  @media (max-width: 700px) {
    height: calc(100vh - 160px);
    height: calc(100dvh - 160px);
    min-height: 0;
    border-radius: 8px;
  }
`;

const ConvList = styled.div`
  width: 290px;
  min-width: 200px;
  border-right: 1px solid ${C.border};
  overflow-y: auto;
  background: ${C.bg};
  @media (max-width: 700px) {
    width: ${({ show }) => (show ? "100%" : "0")};
    min-width: ${({ show }) => (show ? "100%" : "0")};
    display: ${({ show }) => (show ? "block" : "none")};
  }
`;

const ConvItem = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${C.border};
  cursor: pointer;
  background: ${({ active }) => (active ? C.primaryLight : "transparent")};
  border-left: 3px solid ${({ active }) => (active ? C.primary : "transparent")};
  transition: all 0.1s;
  &:hover { background: ${({ active }) => (active ? C.primaryLight : "#f1f5f9")}; }
`;

const ConvName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${C.text};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ConvPreview = styled.div`
  font-size: 12px;
  color: ${C.muted};
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  @media (max-width: 700px) {
    display: ${({ show }) => (show ? "flex" : "none")};
  }
`;

const ChatHeader = styled.div`
  padding: 14px 20px;
  background: white;
  border-bottom: 1px solid ${C.border};
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: ${C.shadow};
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Bubble = styled.div`
  max-width: 68%;
  padding: 10px 14px;
  border-radius: ${({ out }) => (out ? "14px 14px 4px 14px" : "14px 14px 14px 4px")};
  background: ${({ out }) => (out ? C.waBubbleOut : C.waBubbleIn)};
  align-self: ${({ out }) => (out ? "flex-end" : "flex-start")};
  font-size: 14px;
  line-height: 1.5;
  box-shadow: ${C.shadow};
  border: 1px solid ${({ out }) => (out ? "#ddd6fe" : C.border)};
  @media (max-width: 700px) { max-width: 85%; }
`;

const BubbleTime = styled.div`
  font-size: 10px;
  color: ${C.muted};
  margin-top: 4px;
  text-align: right;
`;

const ChatInputBar = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  padding-bottom: max(14px, env(safe-area-inset-bottom, 14px));
  background: white;
  border-top: 1px solid ${C.border};
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 10px 16px;
  border: 1.5px solid ${C.border};
  border-radius: 24px;
  font-size: 14px;
  font-family: inherit;
  background: ${C.bg};
  color: ${C.text};
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: ${C.primary}; background: white; }
  &::placeholder { color: ${C.muted}; }
`;

const EmptyChat = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${C.muted};
  gap: 8px;
`;

// ─── Emoji ────────────────────────────────────────────────────────────────────
const EMOJIS = [
  "😀","😂","🥰","😍","😊","😎","🤔","😅","😭","😡","😇","🥲",
  "👍","👎","👋","🙏","👏","🤝","💪","🤞","❤️","🔥","⭐","✅",
  "❌","⚠️","🎉","🎊","🎁","🏆","📌","💰","📱","💻","📷","🔍",
  "🍕","🍔","☕","🍺","🎶","🏠","🚗","✈️","🛒","📦","🚚","⏰",
];

function EmojiPickerPopup({ onSelect }) {
  return (
    <div style={{
      position: "absolute", bottom: "calc(100% + 6px)", left: 0,
      background: "white", border: `1px solid ${C.border}`, borderRadius: 12,
      padding: 10, boxShadow: C.shadowLg, zIndex: 200,
      display: "flex", flexWrap: "wrap", gap: 2, width: 264,
    }}>
      {EMOJIS.map(e => (
        <button key={e} onMouseDown={ev => { ev.preventDefault(); onSelect(e); }} style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 22, width: 38, height: 38, borderRadius: 6,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.1s",
        }}
        onMouseEnter={ev => ev.currentTarget.style.background = C.bg}
        onMouseLeave={ev => ev.currentTarget.style.background = "none"}
        >{e}</button>
      ))}
    </div>
  );
}

// ─── MediaChatInputBar ────────────────────────────────────────────────────────
function MediaChatInputBar({ onSend, noPhone }) {
  const [testo, setTesto]     = useState("");
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [showEmoji, setEmoji] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef               = useRef(null);
  const cameraRef             = useRef(null);
  const wrapRef               = useRef(null);

  useEffect(() => {
    if (!showEmoji) return;
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setEmoji(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmoji]);

  function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
    e.target.value = "";
  }

  function removeFile() {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null); setPreview(null);
  }

  async function send() {
    if (!file && !testo.trim()) return;
    setSending(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", testo);
        await onSend({ fd });
      } else {
        await onSend({ message: testo });
      }
      setTesto(""); removeFile(); setEmoji(false);
    } catch {}
    finally { setSending(false); }
  }

  if (noPhone) return (
    <div style={{ padding: 16, textAlign: "center", color: C.muted, fontSize: 13, background: "white", borderTop: `1px solid ${C.border}` }}>
      Numero telefono non impostato
    </div>
  );

  return (
    <div style={{ background: "white", borderTop: `1px solid ${C.border}` }}>
      {file && (
        <div style={{ padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          {preview
            ? <img src={preview} alt="" style={{ height: 64, borderRadius: 8, objectFit: "cover" }} />
            : <span style={{ fontSize: 13, color: C.text }}>📄 <strong>{file.name}</strong> <span style={{ color: C.muted }}>({(file.size/1024).toFixed(0)} KB)</span></span>
          }
          <button onClick={removeFile} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 18 }}>✕</button>
        </div>
      )}
      <div ref={wrapRef} style={{ display: "flex", gap: 4, padding: "10px 12px", alignItems: "center", position: "relative" }}>
        <button onClick={() => setEmoji(s => !s)} title="Emoji"
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, padding: "3px 5px", borderRadius: 6, color: showEmoji ? C.primary : C.muted, flexShrink: 0 }}>😊</button>
        <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip" style={{ display: "none" }} onChange={handleFile} />
        <button onClick={() => fileRef.current.click()} title="Allega file"
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: "3px 5px", borderRadius: 6, color: C.muted, flexShrink: 0 }}>📎</button>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFile} />
        <button onClick={() => cameraRef.current.click()} title="Fotocamera"
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: "3px 5px", borderRadius: 6, color: C.muted, flexShrink: 0 }}>📷</button>
        <ChatInput
          value={testo}
          onChange={e => setTesto(e.target.value)}
          placeholder={file ? "Aggiungi didascalia…" : "Scrivi un messaggio…"}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          onFocus={e => { setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "nearest" }), 300); }}
        />
        <Btn variant="wa" sm onClick={send} disabled={sending || (!testo.trim() && !file)}>
          <WhatsAppIcon size={13} color="white" />{sending ? "…" : "Invia"}
        </Btn>
        {showEmoji && <EmojiPickerPopup onSelect={e => { setTesto(t => t + e); }} />}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const token = () => localStorage.getItem("token");
const authH = () => ({ headers: { Authorization: `Bearer ${token()}` } });

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function fmtTs(d) {
  if (!d) return "";
  return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
}

function Avatar({ name, size = 36 }) {
  const initials = name ? name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() : "?";
  const colors = ["#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444"];
  const bg = colors[(name?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, color: "white",
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38,
      fontWeight: 700, flexShrink: 0, fontFamily: "Inter, sans-serif" }}>
      {initials}
    </div>
  );
}

const MOTIVI = ["Prezzi più bassi da altri", "Non risponde", "Prodotto non adatto", "Ha trovato altro fornitore", "Altro"];
const CATEGORIE = ["Ristorante", "Macelleria", "Hotel", "Bar", "Privato", "GDO", "Altro"];

const STATI_DEFAULT = [
  { value: "follow_up", label: "Follow-up", color: "#fef3c7", textColor: "#d97706", fixed: true },
  { value: "cliente",   label: "Cliente",   color: "#d1fae5", textColor: "#059669", fixed: true },
];
const STATO_PALETTE = [
  { color: "#f1f5f9", textColor: "#475569" },
  { color: "#dbeafe", textColor: "#1d4ed8" },
  { color: "#fce7f3", textColor: "#be185d" },
  { color: "#ede9fe", textColor: "#5b21b6" },
  { color: "#ffedd5", textColor: "#c2410c" },
];

function loadCategorie() {
  try { return JSON.parse(localStorage.getItem("crm_categorie")) || [...CATEGORIE]; }
  catch { return [...CATEGORIE]; }
}
function saveCategorie(list) { localStorage.setItem("crm_categorie", JSON.stringify(list)); }

function loadStati() {
  try { return JSON.parse(localStorage.getItem("crm_stati")) || [...STATI_DEFAULT]; }
  catch { return [...STATI_DEFAULT]; }
}
function saveStati(list) { localStorage.setItem("crm_stati", JSON.stringify(list)); }

function StatoPill({ stato }) {
  const s = loadStati().find(x => x.value === stato) || { label: stato, color: "#f1f5f9", textColor: "#475569" };
  return <Pill color={s.color} textColor={s.textColor}>● {s.label}</Pill>;
}

// ─── Modal: Gestisci categorie ────────────────────────────────────────────────
function GestisciCategorieModal({ onClose }) {
  const [lista, setLista] = useState(loadCategorie);
  const [nuova, setNuova] = useState("");

  function aggiungi() {
    const t = nuova.trim();
    if (!t || lista.includes(t)) return;
    setLista(l => [...l, t]);
    setNuova("");
  }

  function salva() { saveCategorie(lista); onClose(); }

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
        <ModalTitle>Gestisci categorie</ModalTitle>
        <div style={{ marginBottom: 14 }}>
          {lista.map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Pill color="#f1f5f9" textColor={C.textSec} style={{ flex: 1, fontSize: 13 }}>{c}</Pill>
              <button onClick={() => setLista(l => l.filter((_, j) => j !== i))}
                style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontSize: 16, lineHeight: 1 }}>✕</button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <Input value={nuova} onChange={e => setNuova(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), aggiungi())}
            placeholder="Nuova categoria…" style={{ flex: 1 }} />
          <Btn sm onClick={aggiungi}>+</Btn>
        </div>
        <Divider />
        <BtnRow style={{ justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
          <Btn onClick={salva}>Salva</Btn>
        </BtnRow>
      </Modal>
    </Overlay>
  );
}

// ─── Modal: Gestisci stati ────────────────────────────────────────────────────
function GestisciStatiModal({ onClose }) {
  const [lista, setLista]       = useState(loadStati);
  const [nuovaLabel, setLabel]  = useState("");
  const [coloreIdx, setColore]  = useState(0);

  function aggiungi() {
    const t = nuovaLabel.trim();
    if (!t) return;
    const value = t.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if (lista.find(s => s.value === value)) return;
    const col = STATO_PALETTE[coloreIdx % STATO_PALETTE.length];
    setLista(l => [...l, { value, label: t, color: col.color, textColor: col.textColor }]);
    setLabel("");
  }

  function salva() { saveStati(lista); onClose(); }

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
        <ModalTitle>Gestisci stati</ModalTitle>
        <div style={{ marginBottom: 14 }}>
          {lista.map(s => (
            <div key={s.value} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Pill color={s.color} textColor={s.textColor} style={{ flex: 1, fontSize: 13 }}>● {s.label}</Pill>
              {s.fixed
                ? <span style={{ width: 22 }} />
                : <button onClick={() => setLista(l => l.filter(x => x.value !== s.value))}
                    style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontSize: 16, lineHeight: 1 }}>✕</button>
              }
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 6 }}>Colore nuovo stato:</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {STATO_PALETTE.map((c, i) => (
            <button key={i} onClick={() => setColore(i)} style={{
              width: 24, height: 24, borderRadius: "50%", background: c.color,
              border: `2px solid ${coloreIdx === i ? c.textColor : C.border}`,
              cursor: "pointer", padding: 0,
            }} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <Input value={nuovaLabel} onChange={e => setLabel(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), aggiungi())}
            placeholder="Nuovo stato…" style={{ flex: 1 }} />
          <Btn sm onClick={aggiungi}>+</Btn>
        </div>
        <Divider />
        <BtnRow style={{ justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
          <Btn onClick={salva}>Salva</Btn>
        </BtnRow>
      </Modal>
    </Overlay>
  );
}

// ─── Modal Cliente ────────────────────────────────────────────────────────────
function ClienteModal({ cliente, onClose, onSave, prefill }) {
  const empty = { nome: prefill?.nome || "", telefono: prefill?.telefono || "", categoria: "", stato: "follow_up", data_ultimo_contatto: "", prossimo_followup: "", azione: "", note: "" };
  const [form, setForm] = useState(
    cliente
      ? { ...cliente, data_ultimo_contatto: cliente.data_ultimo_contatto?.slice(0,10) || "", prossimo_followup: cliente.prossimo_followup?.slice(0,10) || "" }
      : empty
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.nome.trim()) return;
    try {
      if (cliente) {
        await axios.put(`/api/crm/clienti/${cliente.id}`, form, authH());
      } else {
        await axios.post("/api/crm/clienti", form, authH());
      }
      onSave();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.error || err.message));
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalTitle>{cliente ? "Modifica cliente" : "Nuovo cliente"}</ModalTitle>
        <form onSubmit={submit}>
          <FormGrid>
            <div>
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={e => set("nome", e.target.value)} required placeholder="Es. Mario Rossi" />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.telefono} onChange={e => set("telefono", e.target.value)} placeholder="393401234567" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.categoria} onChange={e => set("categoria", e.target.value)}>
                <option value="">— seleziona —</option>
                {loadCategorie().map(c => <option key={c}>{c}</option>)}
              </Select>
            </div>
            <div>
              <Label>Stato</Label>
              <Select value={form.stato} onChange={e => set("stato", e.target.value)}>
                {loadStati().map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Ultimo contatto</Label>
              <Input type="date" value={form.data_ultimo_contatto} onChange={e => set("data_ultimo_contatto", e.target.value)} />
            </div>
            <div>
              <Label>Prossimo follow-up</Label>
              <Input type="date" value={form.prossimo_followup} onChange={e => set("prossimo_followup", e.target.value)} />
            </div>
            <div>
              <Label>Azione</Label>
              <Select value={form.azione} onChange={e => set("azione", e.target.value)}>
                <option value="">— nessuna —</option>
                <option value="chiamare">📞 Chiamare</option>
                <option value="whatsapp">💬 WhatsApp</option>
              </Select>
            </div>
            <FullCol>
              <Label>Note</Label>
              <Textarea rows={3} value={form.note} onChange={e => set("note", e.target.value)} placeholder="Note aggiuntive…" />
            </FullCol>
          </FormGrid>
          <Divider />
          <BtnRow style={{ justifyContent: "flex-end" }}>
            <Btn type="button" variant="ghost" onClick={onClose}>Annulla</Btn>
            <Btn type="submit">{cliente ? "Salva modifiche" : "Crea cliente"}</Btn>
          </BtnRow>
        </form>
      </Modal>
    </Overlay>
  );
}

// ─── Modal Perso ──────────────────────────────────────────────────────────────
function PersoModal({ clienti, onClose, onSave }) {
  const [form, setForm] = useState({ client_id: "", nome_contatto: "", telefono: "", motivo: "", note: "", data: new Date().toISOString().slice(0,10) });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleClientChange(e) {
    const id = e.target.value;
    set("client_id", id);
    if (id) {
      const c = clienti.find(c => String(c.id) === id);
      if (c) { set("nome_contatto", c.nome); set("telefono", c.telefono || ""); }
    }
  }

  async function submit(e) {
    e.preventDefault();
    try {
      await axios.post("/api/crm/persi", form, authH());
      onSave();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.error || err.message));
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalTitle>Aggiungi trattativa persa</ModalTitle>
        <form onSubmit={submit}>
          <FormGrid>
            <div>
              <Label>Cliente (opzionale)</Label>
              <Select value={form.client_id} onChange={handleClientChange}>
                <option value="">— nessuno —</option>
                {clienti.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </Select>
            </div>
            <div>
              <Label>Nome contatto</Label>
              <Input value={form.nome_contatto} onChange={e => set("nome_contatto", e.target.value)} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.telefono} onChange={e => set("telefono", e.target.value)} />
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={form.data} onChange={e => set("data", e.target.value)} />
            </div>
            <div>
              <Label>Motivo</Label>
              <Select value={form.motivo} onChange={e => set("motivo", e.target.value)}>
                <option value="">— seleziona —</option>
                {MOTIVI.map(m => <option key={m}>{m}</option>)}
              </Select>
            </div>
            <FullCol>
              <Label>Note</Label>
              <Textarea rows={3} value={form.note} onChange={e => set("note", e.target.value)} />
            </FullCol>
          </FormGrid>
          <Divider />
          <BtnRow style={{ justifyContent: "flex-end" }}>
            <Btn type="button" variant="ghost" onClick={onClose}>Annulla</Btn>
            <Btn type="submit">Aggiungi</Btn>
          </BtnRow>
        </form>
      </Modal>
    </Overlay>
  );
}

// ─── Modal Invia Template (bulk) ─────────────────────────────────────────────
function InviaTemplateModal({ clienti, onClose, onDone }) {
  const [templates, setTemplates]   = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [selected, setSelected]     = useState(null);
  const [stato, setStato]           = useState("idle"); // idle | sending | done
  const [results, setResults]       = useState([]);     // { cliente, ok, error }
  const [progress, setProgress]     = useState(0);
  const [tplError, setTplError]     = useState("");

  useEffect(() => {
    axios.get("/api/whatsapp/templates", authH())
      .then(({ data }) => { setTemplates(data); if (data.length) setSelected(data[0]); })
      .catch(() => setTplError("Impossibile caricare i template"))
      .finally(() => setLoadingTpl(false));
  }, []);

  const conTelefono = clienti.filter(c => c.telefono);
  const senzaTelefono = clienti.filter(c => !c.telefono);

  async function send() {
    if (!selected || !conTelefono.length) return;
    setStato("sending");
    const res = [];
    for (let i = 0; i < conTelefono.length; i++) {
      const c = conTelefono[i];
      try {
        await axios.post("/api/whatsapp/send-template", {
          to: c.telefono,
          templateName: selected.name,
          language: selected.language,
          clientId: c.id,
        }, authH());
        res.push({ cliente: c, ok: true });
      } catch (err) {
        res.push({ cliente: c, ok: false, error: err.response?.data?.error || err.message });
      }
      setProgress(Math.round(((i + 1) / conTelefono.length) * 100));
      setResults([...res]);
      // piccola pausa per non sovraccaricare l'API Meta
      if (i < conTelefono.length - 1) await new Promise(r => setTimeout(r, 300));
    }
    setStato("done");
  }

  const inviati = results.filter(r => r.ok).length;
  const falliti = results.filter(r => !r.ok).length;

  return (
    <Overlay onClick={stato === "done" ? onDone : onClose}>
      <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <ModalTitle>
          <WhatsAppIcon size={20} color={C.waGreen} /> Invia template
        </ModalTitle>

        {/* Selezione template */}
        {stato === "idle" && (
          <>
            <div style={{ marginBottom: 20, padding: "10px 14px", background: C.primaryLight, borderRadius: 8, fontSize: 13, color: C.primary }}>
              Il messaggio verrà inviato a <strong>{conTelefono.length}</strong> {conTelefono.length === 1 ? "destinatario" : "destinatari"}.
              {senzaTelefono.length > 0 && <span style={{ color: C.warn }}> {senzaTelefono.length} senza numero verranno saltati.</span>}
            </div>

            {loadingTpl && <div style={{ textAlign: "center", color: C.muted, padding: "20px 0" }}>Caricamento template…</div>}
            {tplError && <div style={{ color: C.danger, fontSize: 13 }}>{tplError}</div>}

            {!loadingTpl && templates.length > 0 && (
              <>
                <Label>Template</Label>
                <Select
                  value={selected?.name || ""}
                  onChange={e => setSelected(templates.find(t => t.name === e.target.value))}
                  style={{ marginBottom: 16 }}
                >
                  {templates.map(t => (
                    <option key={t.name} value={t.name}>{t.name.replace(/_/g, " ")}</option>
                  ))}
                </Select>

                {selected?.preview && (
                  <div style={{ background: C.waBubbleOut, border: `1px solid #ddd6fe`, borderRadius: 10, padding: "12px 16px", fontSize: 14, lineHeight: 1.6, color: C.text, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Anteprima</div>
                    {selected.preview}
                  </div>
                )}

                <Label style={{ marginBottom: 8 }}>Destinatari ({conTelefono.length})</Label>
                <div style={{ maxHeight: 140, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {conTelefono.map(c => (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 5, background: C.bg, borderRadius: 20, padding: "3px 10px", fontSize: 13 }}>
                      <Avatar name={c.nome} size={18} />
                      {c.nome}
                    </div>
                  ))}
                </div>
              </>
            )}

            {!loadingTpl && templates.length === 0 && !tplError && (
              <div style={{ textAlign: "center", color: C.muted, padding: "20px 0" }}>Nessun template approvato trovato.</div>
            )}

            <Divider />
            <BtnRow style={{ justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
              <Btn variant="wa" onClick={send} disabled={!selected || loadingTpl || conTelefono.length === 0}>
                <WhatsAppIcon size={14} color="white" /> Invia a {conTelefono.length} contatti
              </Btn>
            </BtnRow>
          </>
        )}

        {/* Invio in corso */}
        {stato === "sending" && (
          <div style={{ padding: "8px 0" }}>
            <div style={{ fontSize: 14, color: C.textSec, marginBottom: 4 }}>
              Invio in corso… {results.length}/{conTelefono.length}
            </div>
            <ProgressBar><ProgressFill pct={progress} /></ProgressBar>
            <div style={{ fontSize: 12, color: C.muted, textAlign: "right" }}>{progress}%</div>
            <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
              {results.map(r => (
                <div key={r.cliente.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <span>{r.ok ? "✅" : "❌"}</span>
                  <Avatar name={r.cliente.nome} size={20} />
                  <span style={{ flex: 1 }}>{r.cliente.nome}</span>
                  {!r.ok && <span style={{ color: C.danger, fontSize: 12 }}>{r.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risultati finali */}
        {stato === "done" && (
          <div>
            <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1, background: C.successLight, borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.success }}>{inviati}</div>
                <div style={{ fontSize: 13, color: C.success }}>Inviati</div>
              </div>
              {falliti > 0 && (
                <div style={{ flex: 1, background: C.dangerLight, borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: C.danger }}>{falliti}</div>
                  <div style={{ fontSize: 13, color: C.danger }}>Falliti</div>
                </div>
              )}
            </div>
            {falliti > 0 && (
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                {results.filter(r => !r.ok).map(r => (
                  <div key={r.cliente.id}>❌ {r.cliente.nome}: {r.error}</div>
                ))}
              </div>
            )}
            <Btn onClick={onDone} style={{ width: "100%" }}>Chiudi</Btn>
          </div>
        )}
      </Modal>
    </Overlay>
  );
}

// ─── Chat Modal (da lista clienti) ───────────────────────────────────────────
function ChatModal({ cliente, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/crm/messaggi-cliente/${cliente.id}`, authH());
      setMsgs(data);
    } catch {}
  }, [cliente.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  async function onSend({ message, fd }) {
    if (fd) {
      fd.append("to", cliente.telefono);
      fd.append("clientId", cliente.id);
      await axios.post("/api/whatsapp/send-media", fd, { headers: { Authorization: `Bearer ${token()}` } });
    } else {
      await axios.post("/api/whatsapp/send", { to: cliente.telefono, message, clientId: cliente.id }, authH());
    }
    await load();
  }

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: 560, padding: 0, overflow: "hidden" }}>
        <ChatHeader style={{ background: C.primary }}>
          <Avatar name={cliente.nome} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: "white", fontSize: 15 }}>{cliente.nome}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>{cliente.telefono}</div>
          </div>
          <Btn sm variant="ghost" onClick={onClose} style={{ color: "white", border: "none", padding: "4px 8px" }}>✕</Btn>
        </ChatHeader>

        <div style={{ height: 380, overflowY: "auto", padding: 16, background: "#f8fafc", display: "flex", flexDirection: "column", gap: 8 }}>
          {msgs.length === 0 && (
            <div style={{ textAlign: "center", color: C.muted, marginTop: 60, fontSize: 14 }}>
              Nessun messaggio ancora
            </div>
          )}
          {msgs.map(m => (
            <Bubble key={m.id} out={m.direction === "outbound"}>
              {m.body}
              <BubbleTime>{fmtTs(m.timestamp)}</BubbleTime>
            </Bubble>
          ))}
          <div ref={bottomRef} />
        </div>

        <MediaChatInputBar onSend={onSend} noPhone={!cliente.telefono} />
      </Modal>
    </Overlay>
  );
}

// ─── Modal Nuova Chat ─────────────────────────────────────────────────────────
function NuovaChatModal({ clienti, onClose, onDone }) {
  const [templates, setTemplates]       = useState([]);
  const [loadingTpl, setLoadingTpl]     = useState(true);
  const [tplError, setTplError]         = useState("");
  const [selectedTpl, setSelectedTpl]   = useState(null);
  const [search, setSearch]             = useState("");
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [stato, setStato]               = useState("idle");
  const [results, setResults]           = useState([]);
  const [progress, setProgress]         = useState(0);

  useEffect(() => {
    axios.get("/api/whatsapp/templates", authH())
      .then(({ data }) => { setTemplates(data); if (data.length) setSelectedTpl(data[0]); })
      .catch(() => setTplError("Impossibile caricare i template"))
      .finally(() => setLoadingTpl(false));
  }, []);

  const filtered = clienti.filter(c =>
    c.nome.toLowerCase().includes(search.toLowerCase()) ||
    (c.categoria || "").toLowerCase().includes(search.toLowerCase())
  );
  const allChecked = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id));

  function toggleAll() {
    if (allChecked) {
      setSelectedIds(s => { const n = new Set(s); filtered.forEach(c => n.delete(c.id)); return n; });
    } else {
      setSelectedIds(s => { const n = new Set(s); filtered.forEach(c => n.add(c.id)); return n; });
    }
  }
  function toggle(id) {
    setSelectedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const destinatari = clienti.filter(c => selectedIds.has(c.id) && c.telefono);
  const senzaTelefono = clienti.filter(c => selectedIds.has(c.id) && !c.telefono);

  async function send() {
    if (!selectedTpl || !destinatari.length) return;
    setStato("sending");
    const res = [];
    for (let i = 0; i < destinatari.length; i++) {
      const c = destinatari[i];
      try {
        await axios.post("/api/whatsapp/send-template", {
          to: c.telefono,
          templateName: selectedTpl.name,
          language: selectedTpl.language,
          clientId: c.id,
        }, authH());
        res.push({ cliente: c, ok: true });
      } catch (err) {
        res.push({ cliente: c, ok: false, error: err.response?.data?.error || err.message });
      }
      setProgress(Math.round(((i + 1) / destinatari.length) * 100));
      setResults([...res]);
      if (i < destinatari.length - 1) await new Promise(r => setTimeout(r, 300));
    }
    setStato("done");
  }

  const inviati = results.filter(r => r.ok).length;
  const falliti = results.filter(r => !r.ok).length;

  return (
    <Overlay onClick={stato === "done" ? onDone : onClose}>
      <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <ModalTitle>
          <WhatsAppIcon size={20} color={C.waGreen} /> Nuova chat — invia template
        </ModalTitle>

        {stato === "idle" && (
          <>
            {/* Template */}
            <div style={{ marginBottom: 18 }}>
              <Label>Template</Label>
              {loadingTpl && <div style={{ color: C.muted, fontSize: 13, padding: "8px 0" }}>Caricamento…</div>}
              {tplError && <div style={{ color: C.danger, fontSize: 13 }}>{tplError}</div>}
              {!loadingTpl && templates.length > 0 && (
                <>
                  <Select
                    value={selectedTpl?.name || ""}
                    onChange={e => setSelectedTpl(templates.find(t => t.name === e.target.value))}
                    style={{ marginBottom: 10 }}
                  >
                    {templates.map(t => (
                      <option key={t.name} value={t.name}>{t.name.replace(/_/g, " ")}</option>
                    ))}
                  </Select>
                  {selectedTpl?.preview && (
                    <div style={{ background: C.waBubbleOut, border: `1px solid #ddd6fe`, borderRadius: 10, padding: "10px 14px", fontSize: 13, lineHeight: 1.6, color: C.text }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>Anteprima</div>
                      {selectedTpl.preview}
                    </div>
                  )}
                </>
              )}
              {!loadingTpl && templates.length === 0 && !tplError && (
                <div style={{ color: C.muted, fontSize: 13 }}>Nessun template approvato trovato.</div>
              )}
            </div>

            <Divider />

            {/* Selezione clienti */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <Label style={{ margin: 0 }}>Clienti destinatari</Label>
                <SearchWrap>
                  <SearchIcon>🔍</SearchIcon>
                  <SearchInput
                    placeholder="Cerca…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ width: 180 }}
                  />
                </SearchWrap>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, maxHeight: 240, overflowY: "auto" }}>
                {filtered.length === 0 ? (
                  <div style={{ padding: "20px 0", textAlign: "center", color: C.muted, fontSize: 13 }}>Nessun cliente trovato</div>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th style={{ width: 36, padding: "10px 12px" }}>
                          <Checkbox checked={allChecked} onChange={toggleAll} />
                        </Th>
                        <Th style={{ padding: "10px 12px" }}>Cliente</Th>
                        <Th style={{ padding: "10px 12px" }}>Telefono</Th>
                        <Th style={{ padding: "10px 12px" }}>Categoria</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(c => (
                        <Tr key={c.id} style={{ background: selectedIds.has(c.id) ? C.primaryLight : undefined }} onClick={() => toggle(c.id)}>
                          <Td style={{ width: 36, padding: "8px 12px" }}>
                            <Checkbox checked={selectedIds.has(c.id)} onChange={() => toggle(c.id)} onClick={e => e.stopPropagation()} />
                          </Td>
                          <Td style={{ padding: "8px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <Avatar name={c.nome} size={26} />
                              <span style={{ fontWeight: 600, fontSize: 13 }}>{c.nome}</span>
                            </div>
                          </Td>
                          <Td style={{ padding: "8px 12px", fontSize: 12, color: c.telefono ? C.text : C.muted }}>
                            {c.telefono || "—"}
                          </Td>
                          <Td style={{ padding: "8px 12px" }}>
                            {c.categoria
                              ? <Pill color="#f1f5f9" textColor={C.textSec} style={{ fontSize: 11 }}>{c.categoria}</Pill>
                              : <span style={{ color: C.muted, fontSize: 12 }}>—</span>}
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
              {selectedIds.size > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: C.textSec }}>
                  <strong>{destinatari.length}</strong> con numero selezionati
                  {senzaTelefono.length > 0 && <span style={{ color: C.warn }}> · {senzaTelefono.length} senza numero verranno saltati</span>}
                </div>
              )}
            </div>

            <Divider />
            <BtnRow style={{ justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
              <Btn
                variant="wa"
                onClick={send}
                disabled={!selectedTpl || loadingTpl || destinatari.length === 0}
              >
                <WhatsAppIcon size={14} color="white" /> Invia a {destinatari.length} contatti
              </Btn>
            </BtnRow>
          </>
        )}

        {stato === "sending" && (
          <div style={{ padding: "8px 0" }}>
            <div style={{ fontSize: 14, color: C.textSec, marginBottom: 4 }}>
              Invio in corso… {results.length}/{destinatari.length}
            </div>
            <ProgressBar><ProgressFill pct={progress} /></ProgressBar>
            <div style={{ fontSize: 12, color: C.muted, textAlign: "right" }}>{progress}%</div>
            <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
              {results.map(r => (
                <div key={r.cliente.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <span>{r.ok ? "✅" : "❌"}</span>
                  <Avatar name={r.cliente.nome} size={20} />
                  <span style={{ flex: 1 }}>{r.cliente.nome}</span>
                  {!r.ok && <span style={{ color: C.danger, fontSize: 12 }}>{r.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {stato === "done" && (
          <div>
            <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1, background: C.successLight, borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.success }}>{inviati}</div>
                <div style={{ fontSize: 13, color: C.success }}>Inviati</div>
              </div>
              {falliti > 0 && (
                <div style={{ flex: 1, background: C.dangerLight, borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: C.danger }}>{falliti}</div>
                  <div style={{ fontSize: 13, color: C.danger }}>Falliti</div>
                </div>
              )}
            </div>
            {falliti > 0 && (
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                {results.filter(r => !r.ok).map(r => (
                  <div key={r.cliente.id}>❌ {r.cliente.nome}: {r.error}</div>
                ))}
              </div>
            )}
            <Btn onClick={onDone} style={{ width: "100%" }}>Chiudi</Btn>
          </div>
        )}
      </Modal>
    </Overlay>
  );
}

// ─── Tab: Clienti ─────────────────────────────────────────────────────────────
function TabClienti({ clienti, reload }) {
  const [modal, setModal]             = useState(null);
  const [chat, setChat]               = useState(null);
  const [showBulk, setShowBulk]       = useState(false);
  const [showNuovaChat, setShowNuovaChat] = useState(false);
  const [showCatModal, setShowCatModal]   = useState(false);
  const [showStatoModal, setShowStatoModal] = useState(false);
  const [, forceUpdate]               = useState(0);
  const [search, setSearch]           = useState("");
  const [selected, setSelected]       = useState(new Set());

  async function del(id) {
    if (!window.confirm("Eliminare questo cliente?")) return;
    await axios.delete(`/api/crm/clienti/${id}`, authH());
    reload();
  }

  const filtered = clienti.filter(c =>
    c.nome.toLowerCase().includes(search.toLowerCase()) ||
    (c.categoria || "").toLowerCase().includes(search.toLowerCase())
  );

  const allChecked = filtered.length > 0 && filtered.every(c => selected.has(c.id));

  function toggleAll() {
    if (allChecked) {
      setSelected(s => { const n = new Set(s); filtered.forEach(c => n.delete(c.id)); return n; });
    } else {
      setSelected(s => { const n = new Set(s); filtered.forEach(c => n.add(c.id)); return n; });
    }
  }

  function toggle(id) {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const selectedClienti = clienti.filter(c => selected.has(c.id));

  return (
    <div>
      <TopBar>
        <SearchWrap>
          <SearchIcon>🔍</SearchIcon>
          <SearchInput placeholder="Cerca cliente…" value={search} onChange={e => setSearch(e.target.value)} />
        </SearchWrap>
        <BtnRow>
          <Btn sm variant="ghost" onClick={() => setShowCatModal(true)} style={{ color: C.textSec }}>🏷 Categorie</Btn>
          <Btn sm variant="ghost" onClick={() => setShowStatoModal(true)} style={{ color: C.textSec }}>🔵 Stati</Btn>
          <Btn variant="outline" onClick={() => setShowNuovaChat(true)}>
            <WhatsAppIcon size={14} color={C.primary} /> Nuova chat
          </Btn>
          <Btn onClick={() => setModal("new")}>+ Nuovo cliente</Btn>
        </BtnRow>
      </TopBar>

      <Card>
        {filtered.length === 0 ? (
          <EmptyState>Nessun cliente trovato</EmptyState>
        ) : (
          <>
            <DesktopOnly>
              <div style={{ overflowX: "auto" }}>
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: 36 }}>
                        <Checkbox checked={allChecked} onChange={toggleAll} />
                      </Th>
                      <Th>Cliente</Th>
                      <Th>Categoria</Th>
                      <Th>Stato</Th>
                      <Th>Ultimo contatto</Th>
                      <Th>Prossimo FU</Th>
                      <Th>Azione</Th>
                      <Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => (
                      <Tr key={c.id} style={{ background: selected.has(c.id) ? C.primaryLight : undefined }}>
                        <Td style={{ width: 36 }}>
                          <Checkbox checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                        </Td>
                        <Td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Avatar name={c.nome} size={32} />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{c.nome}</div>
                              {c.telefono && <div style={{ fontSize: 12, color: C.muted }}>{c.telefono}</div>}
                            </div>
                          </div>
                        </Td>
                        <Td>
                          {c.categoria
                            ? <Pill color="#f1f5f9" textColor={C.textSec}>{c.categoria}</Pill>
                            : <span style={{ color: C.muted }}>—</span>}
                        </Td>
                        <Td><StatoPill stato={c.stato} /></Td>
                        <Td style={{ color: C.textSec, fontSize: 13 }}>{fmtDate(c.data_ultimo_contatto)}</Td>
                        <Td style={{ fontSize: 13 }}>
                          {c.prossimo_followup && new Date(c.prossimo_followup) <= new Date()
                            ? <span style={{ color: C.danger, fontWeight: 600 }}>⚠ {fmtDate(c.prossimo_followup)}</span>
                            : <span style={{ color: C.textSec }}>{fmtDate(c.prossimo_followup)}</span>}
                        </Td>
                        <Td>
                          {c.azione === "whatsapp" && <Pill color={C.successLight} textColor={C.success}><WhatsAppIcon size={11} color={C.success} /> WhatsApp</Pill>}
                          {c.azione === "chiamare" && <Pill color="#dbeafe" textColor="#1d4ed8">📞 Chiamare</Pill>}
                          {!c.azione && <span style={{ color: C.muted }}>—</span>}
                        </Td>
                        <Td>
                          <BtnRow>
                            {c.telefono && (
                              <Btn sm variant="ghost" onClick={() => setChat(c)} style={{ color: C.waGreen, borderColor: C.waGreen }} title="Apri chat">
                                <WhatsAppIcon size={13} color={C.waGreen} />
                              </Btn>
                            )}
                            <Btn sm variant="ghost" onClick={() => setModal(c)}>✏️</Btn>
                            <Btn sm variant="ghost" onClick={() => del(c.id)} style={{ color: C.danger, borderColor: C.border }}>🗑</Btn>
                          </BtnRow>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </DesktopOnly>
            <MobileOnly>
              {filtered.map(c => (
                <div key={c.id} style={{
                  padding: "14px 16px",
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  background: selected.has(c.id) ? C.primaryLight : undefined,
                }}>
                  <div onClick={() => toggle(c.id)} style={{ flexShrink: 0 }}>
                    <Avatar name={c.nome} size={44} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }} onClick={() => toggle(c.id)}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{c.nome}</div>
                    {c.telefono && <div style={{ fontSize: 13, color: C.muted, marginTop: 1 }}>{c.telefono}</div>}
                    <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <StatoPill stato={c.stato} />
                      {c.categoria && <Pill color="#f1f5f9" textColor={C.textSec}>{c.categoria}</Pill>}
                      {c.prossimo_followup && new Date(c.prossimo_followup) <= new Date() &&
                        <Pill color={C.dangerLight} textColor={C.danger}>⚠ {fmtDate(c.prossimo_followup)}</Pill>}
                      {c.azione === "whatsapp" && <Pill color={C.successLight} textColor={C.success}><WhatsAppIcon size={10} color={C.success} /> WA</Pill>}
                      {c.azione === "chiamare" && <Pill color="#dbeafe" textColor="#1d4ed8">📞</Pill>}
                    </div>
                  </div>
                  <BtnRow style={{ flexShrink: 0, flexDirection: "column", gap: 6 }}>
                    {c.telefono && (
                      <Btn sm variant="ghost" onClick={() => setChat(c)} style={{ color: C.waGreen, borderColor: C.waGreen }}>
                        <WhatsAppIcon size={14} color={C.waGreen} />
                      </Btn>
                    )}
                    <Btn sm variant="ghost" onClick={() => setModal(c)}>✏️</Btn>
                    <Btn sm variant="ghost" onClick={() => del(c.id)} style={{ color: C.danger }}>🗑</Btn>
                  </BtnRow>
                </div>
              ))}
            </MobileOnly>
          </>
        )}
      </Card>

      {selected.size > 0 && (
        <SelectionBar>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{selected.size} {selected.size === 1 ? "selezionato" : "selezionati"}</span>
          <Btn sm variant="wa" onClick={() => setShowBulk(true)}>
            <WhatsAppIcon size={13} color="white" /> Invia template
          </Btn>
          <Btn sm variant="ghost" onClick={() => setSelected(new Set())} style={{ color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)" }}>
            Deseleziona
          </Btn>
        </SelectionBar>
      )}

      {modal && (
        <ClienteModal
          cliente={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); reload(); }}
        />
      )}
      {chat && <ChatModal cliente={chat} onClose={() => setChat(null)} />}
      {showCatModal && (
        <GestisciCategorieModal onClose={() => { setShowCatModal(false); forceUpdate(n => n + 1); }} />
      )}
      {showStatoModal && (
        <GestisciStatiModal onClose={() => { setShowStatoModal(false); forceUpdate(n => n + 1); }} />
      )}
      {showBulk && (
        <InviaTemplateModal
          clienti={selectedClienti}
          onClose={() => setShowBulk(false)}
          onDone={() => { setShowBulk(false); setSelected(new Set()); }}
        />
      )}
      {showNuovaChat && (
        <NuovaChatModal
          clienti={clienti}
          onClose={() => setShowNuovaChat(false)}
          onDone={() => setShowNuovaChat(false)}
        />
      )}
    </div>
  );
}

// ─── Tab: Follow-up ───────────────────────────────────────────────────────────
function TabFollowup() {
  const [items, setItems]       = useState([]);
  const [modal, setModal]       = useState(null);
  const [chat, setChat]         = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    const { data } = await axios.get("/api/crm/followup", authH());
    setItems(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const allChecked = items.length > 0 && items.every(c => selected.has(c.id));

  function toggleAll() {
    if (allChecked) {
      setSelected(s => { const n = new Set(s); items.forEach(c => n.delete(c.id)); return n; });
    } else {
      setSelected(s => { const n = new Set(s); items.forEach(c => n.add(c.id)); return n; });
    }
  }

  function toggle(id) {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const selectedClienti = items.filter(c => selected.has(c.id));

  return (
    <div>
      <TopBar>
        <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Follow-up in scadenza</span>
        <Btn onClick={() => setModal("new")}>+ Nuovo cliente</Btn>
      </TopBar>

      <Card>
        {items.length === 0 ? (
          <EmptyState>Nessun follow-up in programma 🎉</EmptyState>
        ) : (
          <>
            <DesktopOnly>
              <div style={{ overflowX: "auto" }}>
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: 36 }}>
                        <Checkbox checked={allChecked} onChange={toggleAll} />
                      </Th>
                      <Th>Cliente</Th>
                      <Th>Categoria</Th>
                      <Th>Prossimo FU</Th>
                      <Th>Azione</Th>
                      <Th>Note</Th>
                      <Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(c => {
                      const scaduto = c.prossimo_followup && new Date(c.prossimo_followup) < new Date();
                      return (
                        <Tr key={c.id} style={{ background: selected.has(c.id) ? C.primaryLight : scaduto ? "#fff5f5" : "transparent" }}>
                          <Td style={{ width: 36 }}>
                            <Checkbox checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                          </Td>
                          <Td>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <Avatar name={c.nome} size={32} />
                              <span style={{ fontWeight: 600 }}>{c.nome}</span>
                            </div>
                          </Td>
                          <Td>
                            {c.categoria
                              ? <Pill color="#f1f5f9" textColor={C.textSec}>{c.categoria}</Pill>
                              : <span style={{ color: C.muted }}>—</span>}
                          </Td>
                          <Td>
                            {scaduto
                              ? <Pill color={C.dangerLight} textColor={C.danger}>⚠ {fmtDate(c.prossimo_followup)}</Pill>
                              : <span style={{ color: C.textSec, fontSize: 13 }}>{fmtDate(c.prossimo_followup)}</span>}
                          </Td>
                          <Td>
                            {c.azione === "whatsapp" && <Pill color={C.successLight} textColor={C.success}><WhatsAppIcon size={11} color={C.success} /> WhatsApp</Pill>}
                            {c.azione === "chiamare" && <Pill color="#dbeafe" textColor="#1d4ed8">📞 Chiamare</Pill>}
                            {!c.azione && <span style={{ color: C.muted }}>—</span>}
                          </Td>
                          <Td style={{ color: C.muted, fontSize: 13 }}>{c.note || "—"}</Td>
                          <Td>
                            <BtnRow>
                              {c.telefono && (
                                <Btn sm variant="ghost" onClick={() => setChat(c)} style={{ color: C.waGreen, borderColor: C.waGreen }} title="Apri chat">
                                  <WhatsAppIcon size={13} color={C.waGreen} />
                                </Btn>
                              )}
                              <Btn sm variant="ghost" onClick={() => setModal(c)}>✏️</Btn>
                            </BtnRow>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </DesktopOnly>
            <MobileOnly>
              {items.map(c => {
                const scaduto = c.prossimo_followup && new Date(c.prossimo_followup) < new Date();
                return (
                  <div key={c.id} style={{
                    padding: "14px 16px",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    background: scaduto ? "#fff5f5" : undefined,
                  }}>
                    <Avatar name={c.nome} size={44} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{c.nome}</div>
                      {c.telefono && <div style={{ fontSize: 13, color: C.muted, marginTop: 1 }}>{c.telefono}</div>}
                      <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                        {scaduto
                          ? <Pill color={C.dangerLight} textColor={C.danger}>⚠ {fmtDate(c.prossimo_followup)}</Pill>
                          : c.prossimo_followup && <Pill color="#f1f5f9" textColor={C.textSec}>📅 {fmtDate(c.prossimo_followup)}</Pill>}
                        {c.azione === "whatsapp" && <Pill color={C.successLight} textColor={C.success}><WhatsAppIcon size={10} color={C.success} /> WA</Pill>}
                        {c.azione === "chiamare" && <Pill color="#dbeafe" textColor="#1d4ed8">📞 Chiamare</Pill>}
                        {c.categoria && <Pill color="#f1f5f9" textColor={C.textSec}>{c.categoria}</Pill>}
                      </div>
                      {c.note && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{c.note}</div>}
                    </div>
                    <BtnRow style={{ flexShrink: 0, flexDirection: "column", gap: 6 }}>
                      {c.telefono && (
                        <Btn sm variant="ghost" onClick={() => setChat(c)} style={{ color: C.waGreen, borderColor: C.waGreen }}>
                          <WhatsAppIcon size={14} color={C.waGreen} />
                        </Btn>
                      )}
                      <Btn sm variant="ghost" onClick={() => setModal(c)}>✏️</Btn>
                    </BtnRow>
                  </div>
                );
              })}
            </MobileOnly>
          </>
        )}
      </Card>

      {selected.size > 0 && (
        <SelectionBar>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{selected.size} {selected.size === 1 ? "selezionato" : "selezionati"}</span>
          <Btn sm variant="wa" onClick={() => setShowBulk(true)}>
            <WhatsAppIcon size={13} color="white" /> Invia template
          </Btn>
          <Btn sm variant="ghost" onClick={() => setSelected(new Set())} style={{ color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)" }}>
            Deseleziona
          </Btn>
        </SelectionBar>
      )}

      {modal && (
        <ClienteModal
          cliente={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
      {chat && <ChatModal cliente={chat} onClose={() => setChat(null)} />}
      {showBulk && (
        <InviaTemplateModal
          clienti={selectedClienti}
          onClose={() => setShowBulk(false)}
          onDone={() => { setShowBulk(false); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}

// ─── Tab: WhatsApp ────────────────────────────────────────────────────────────
const CLIENTI_MULTI_ORDINE_FE = ["garavello", "brambilla"];
const GIORNO_RE_FE = /^(luned[ìi]|marted[ìi]|mercoled[ìi]|gioved[ìi]|venerd[ìi]|sabato|domenica|domani|dopodomani|\d{1,2}[\/\-]\d{1,2})/i;

function isBlockMsg(body) {
  const lines = (body || "").split("\n").map(l => l.trim()).filter(Boolean);
  if (lines.length < 3) return false;
  if (/^\d/.test(lines[0]) || GIORNO_RE_FE.test(lines[0])) return false;
  return GIORNO_RE_FE.test(lines[1]) || /^\d{1,2}[\/\-\.]\d{1,2}/.test(lines[1]);
}

function isAggiungiMsg(body) {
  return /^\s*aggiungi\s+a(?:d)?\s+\S/i.test(body || "");
}

function TabWhatsapp({ clientiRefresh, onPropostaCreata }) {
  const [convs, setConvs]           = useState([]);
  const [selected, setSelected]     = useState(null);
  const [msgs, setMsgs]             = useState([]);
  const [showList, setShowList]     = useState(true);
  const [nuovoCliente, setNuovoCliente] = useState(null);
  const [analizzando, setAnalizzando]  = useState(false);
  const [analisiOk, setAnalisiOk]      = useState(false);
  const [msgProposteMap, setMsgProposteMap] = useState({}); // msgId → { stato, proposteId }
  const [analizzandoMsg, setAnalizzandoMsg] = useState({}); // msgId → bool
  const bottomRef                   = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/crm/whatsapp-conversazioni", authH());
      setConvs(data);
    } catch {}
  }, []);

  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    loadConvs();
    const iv = setInterval(async () => {
      await loadConvs();
      if (selectedRef.current) {
        try {
          const { data } = await axios.get(
            `/api/crm/messaggi/${encodeURIComponent(selectedRef.current.contact_number)}`, authH()
          );
          setMsgs(data);
        } catch {}
      }
    }, 10000);
    return () => clearInterval(iv);
  }, [loadConvs]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  async function selectConv(conv) {
    setSelected(conv);
    setShowList(false);
    setMsgProposteMap({});
    setAnalizzandoMsg({});
    try {
      const { data } = await axios.get(`/api/crm/messaggi/${encodeURIComponent(conv.contact_number)}`, authH());
      setMsgs(data);
      loadConvs();
      // Carica proposte esistenti con messaggio_id per i contatti multi-ordine
      if (CLIENTI_MULTI_ORDINE_FE.some(k => (conv.nome_display || "").toLowerCase().includes(k))) {
        const { data: proposte } = await axios.get(
          `/api/ia/proposte?contact_number=${encodeURIComponent(conv.contact_number)}`, authH()
        );
        const map = {};
        for (const p of proposte) {
          if (p.messaggio_id != null) {
            map[p.messaggio_id] = { stato: p.stato, proposteId: p.id };
          }
        }
        setMsgProposteMap(map);
      }
    } catch {}
  }

  async function onSend({ message, fd }) {
    if (fd) {
      fd.append("to", selected.contact_number);
      fd.append("clientId", selected.client_id || "");
      await axios.post("/api/whatsapp/send-media", fd, { headers: { Authorization: `Bearer ${token()}` } });
    } else {
      await axios.post("/api/whatsapp/send", {
        to: selected.contact_number,
        message,
        clientId: selected.client_id || null,
      }, authH());
    }
    const { data } = await axios.get(`/api/crm/messaggi/${encodeURIComponent(selected.contact_number)}`, authH());
    setMsgs(data);
  }

  async function analizzaChat() {
    if (!selected) return;
    setAnalizzando(true);
    setAnalisiOk(false);
    try {
      const { data } = await axios.post("/api/ia/analizza-chat", {
        contact_number: selected.contact_number,
        client_id: selected.client_id || null,
      }, authH());

      // Polling ogni 4s finché lo stato esce da in_elaborazione
      const proposteId = data.id;
      const poll = setInterval(async () => {
        try {
          const { data: p } = await axios.get(`/api/ia/proposte/${proposteId}`, authH());
          if (p.stato !== "in_elaborazione") {
            clearInterval(poll);
            setAnalizzando(false);
            if (p.stato === "errore") {
              alert("Errore analisi modello: " + (p.note_ai || "errore sconosciuto"));
            } else {
              setAnalisiOk(true);
              setTimeout(() => setAnalisiOk(false), 3000);
              if (onPropostaCreata) onPropostaCreata();
            }
          }
        } catch { clearInterval(poll); setAnalizzando(false); }
      }, 4000);
    } catch (err) {
      setAnalizzando(false);
      alert("Errore analisi: " + (err.response?.data?.error || err.message));
    }
  }

  async function analizzaChatMsg(msg, isAggiungi) {
    const msgId = msg.id;
    setAnalizzandoMsg(prev => ({ ...prev, [msgId]: true }));
    try {
      const { data } = await axios.post("/api/ia/analizza-chat", {
        contact_number: selected.contact_number,
        client_id: selected.client_id || null,
        singolo_messaggio_id: msgId,
        aggiungi_mode: isAggiungi || false,
      }, authH());
      const proposteId = data.id;
      setMsgProposteMap(prev => ({ ...prev, [msgId]: { stato: "in_elaborazione", proposteId } }));
      const poll = setInterval(async () => {
        try {
          const { data: p } = await axios.get(`/api/ia/proposte/${proposteId}`, authH());
          if (p.stato !== "in_elaborazione") {
            clearInterval(poll);
            setAnalizzandoMsg(prev => ({ ...prev, [msgId]: false }));
            setMsgProposteMap(prev => ({ ...prev, [msgId]: { stato: p.stato, proposteId } }));
            if (p.stato === "errore") {
              alert("Errore: " + (p.note_ai || "errore sconosciuto"));
            } else if (onPropostaCreata) {
              onPropostaCreata();
            }
          }
        } catch { clearInterval(poll); setAnalizzandoMsg(prev => ({ ...prev, [msgId]: false })); }
      }, 4000);
    } catch (err) {
      setAnalizzandoMsg(prev => ({ ...prev, [msgId]: false }));
      alert("Errore: " + (err.response?.data?.error || err.message));
    }
  }

  async function eliminaChat(conv) {
    if (!window.confirm(`Eliminare tutta la conversazione con ${conv.nome_display || conv.contact_number}?`)) return;
    try {
      await axios.delete(`/api/crm/whatsapp-conversazioni/${encodeURIComponent(conv.contact_number)}`, authH());
      if (selected?.contact_number === conv.contact_number) { setSelected(null); setMsgs([]); }
      loadConvs();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.error || err.message));
    }
  }

  const totalNonLetti = convs.reduce((s, c) => s + Number(c.non_letti || 0), 0);
  const isMultiOrdine = CLIENTI_MULTI_ORDINE_FE.some(k => (selected?.nome_display || "").toLowerCase().includes(k));

  return (
    <div>
      {totalNonLetti > 0 && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: C.warnLight, border: `1px solid #fde68a`, borderRadius: 10, fontSize: 14, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>📩</span>
          <strong>{totalNonLetti}</strong> {totalNonLetti === 1 ? "messaggio non letto" : "messaggi non letti"}
        </div>
      )}

      <ChatWrap>
        <ConvList show={showList}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, background: "white" }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: C.textSec }}>
              Conversazioni {convs.length > 0 && <span style={{ color: C.muted, fontWeight: 400 }}>({convs.length})</span>}
            </span>
          </div>
          {convs.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>
              Nessuna conversazione
            </div>
          )}
          {convs.map(c => (
            <ConvItem key={c.contact_number} active={selected?.contact_number === c.contact_number}
              onClick={() => selectConv(c)}
              style={{ position: "relative" }}
            >
              <ConvName>
                <Avatar name={c.nome_display || c.contact_number} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{c.nome_display || c.contact_number}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {Number(c.non_letti) > 0 && <Badge>{c.non_letti}</Badge>}
                      <span
                        title="Elimina chat"
                        onClick={e => { e.stopPropagation(); eliminaChat(c); }}
                        style={{ color: C.muted, cursor: "pointer", fontSize: 14, padding: "2px 4px", borderRadius: 4, lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = C.danger}
                        onMouseLeave={e => e.currentTarget.style.color = C.muted}
                      >🗑</span>
                    </div>
                  </div>
                  <ConvPreview>{c.ultimo_testo || "—"}</ConvPreview>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{fmtTs(c.ultimo_messaggio)}</div>
                </div>
              </ConvName>
            </ConvItem>
          ))}
        </ConvList>

        <ChatArea show={!showList || window.innerWidth > 700}>
          {!selected ? (
            <EmptyChat>
              <span style={{ fontSize: 32 }}>💬</span>
              <span style={{ fontSize: 14 }}>Seleziona una conversazione</span>
            </EmptyChat>
          ) : (
            <>
              <ChatHeader>
                <Btn sm variant="ghost" onClick={() => setShowList(true)}
                  style={{ display: window.innerWidth <= 700 ? "flex" : "none", padding: "4px 8px" }}>←</Btn>
                <Avatar name={selected.nome_display || selected.contact_number} size={36} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>
                    {selected.nome_display || selected.contact_number}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted }}>{selected.contact_number}</div>
                </div>
                {!selected.client_id && (
                  <Btn sm variant="outline" onClick={() => setNuovoCliente({ nome: selected.nome_display || "", telefono: selected.contact_number })}>
                    + Salva come cliente
                  </Btn>
                )}
                <Btn sm variant="outline" onClick={analizzaChat} disabled={analizzando}
                  style={{ borderColor: C.primary, color: analisiOk ? C.success : C.primary }}>
                  {analizzando ? "🤖 Analisi…" : analisiOk ? "✅ Proposta creata" : "🤖 Proponi ordine"}
                </Btn>
              </ChatHeader>

              <ChatMessages>
                {msgs.map(m => {
                  const proposta = isMultiOrdine ? msgProposteMap[m.id] : null;
                  const showBlock = isMultiOrdine && m.direction === "inbound" && isBlockMsg(m.body);
                  const showAggiungi = isMultiOrdine && m.direction === "inbound" && isAggiungiMsg(m.body);
                  const inProgress = !!analizzandoMsg[m.id];
                  return (
                    <React.Fragment key={m.id}>
                      <Bubble out={m.direction === "outbound"}>
                        {m.tipo === "audio" && m.direction === "inbound" ? (
                          <>
                            <div style={{ fontSize: 12, color: C.muted, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                              <span>🎤</span><span>Vocale</span>
                            </div>
                            {m.trascrizione
                              ? <div style={{ fontStyle: "italic", color: C.text }}>{m.trascrizione}</div>
                              : <div style={{ color: C.muted, fontStyle: "italic" }}>[trascrizione non disponibile]</div>
                            }
                          </>
                        ) : m.body}
                        <BubbleTime>{fmtTs(m.timestamp)}</BubbleTime>
                      </Bubble>
                      {(showBlock || showAggiungi) && (
                        <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 6, paddingLeft: 4 }}>
                          {proposta ? (
                            <span style={{
                              fontSize: 11, padding: "3px 10px", borderRadius: 12, fontWeight: 500,
                              background: proposta.stato === "in_elaborazione" ? "#dbeafe" : proposta.stato === "errore" ? "#fee2e2" : proposta.stato === "accettata" ? "#d1fae5" : "#fef3c7",
                              color: proposta.stato === "in_elaborazione" ? "#1e40af" : proposta.stato === "errore" ? "#dc2626" : proposta.stato === "accettata" ? "#059669" : "#d97706",
                            }}>
                              {proposta.stato === "in_elaborazione" ? "🤖 Elaborazione…" : proposta.stato === "errore" ? "❌ Errore" : proposta.stato === "accettata" ? "✅ Confermata" : "⏳ In attesa"}
                            </span>
                          ) : (
                            <Btn sm variant="outline" disabled={inProgress} onClick={() => analizzaChatMsg(m, showAggiungi)}
                              style={{ fontSize: 11, padding: "3px 10px", borderColor: showAggiungi ? C.success : C.primary, color: showAggiungi ? C.success : C.primary }}>
                              {inProgress ? "🤖 Analisi…" : showAggiungi ? "➕ Aggiungi a proposta" : "🤖 Proponi ordine"}
                            </Btn>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
                <div ref={bottomRef} />
              </ChatMessages>

              <MediaChatInputBar onSend={onSend} />
            </>
          )}
        </ChatArea>
      </ChatWrap>

      {nuovoCliente && (
        <ClienteModal
          cliente={null}
          prefill={nuovoCliente}
          onClose={() => setNuovoCliente(null)}
          onSave={() => { setNuovoCliente(null); loadConvs(); clientiRefresh(); }}
        />
      )}
    </div>
  );
}

// ─── Tab: Addestra ────────────────────────────────────────────────────────────
function TabAddestra() {
  const [regole,  setRegole]  = useState([]);
  const [input,   setInput]   = useState("");
  const [adding,  setAdding]  = useState(false);
  const inputRef              = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/ia/regole", authH());
      setRegole(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  async function aggiungi() {
    if (!input.trim()) return;
    setAdding(true);
    try {
      await axios.post("/api/ia/regole", { testo: input.trim() }, authH());
      setInput("");
      await load();
      inputRef.current?.focus();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.error || err.message));
    } finally { setAdding(false); }
  }

  async function toggleAttiva(id) {
    await axios.patch(`/api/ia/regole/${id}`, {}, authH());
    load();
  }

  async function elimina(id) {
    await axios.delete(`/api/ia/regole/${id}`, authH());
    load();
  }

  const attive   = regole.filter(r => r.attiva);
  const inattive = regole.filter(r => !r.attiva);

  return (
    <div style={{ maxWidth: 760 }}>
      {/* Input */}
      <Card style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 26 }}>🧠</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Istruzioni al modello</div>
            <div style={{ fontSize: 13, color: C.muted }}>
              {attive.length} attive · ogni istruzione viene iniettata nel prompt ad ogni analisi
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); aggiungi(); } }}
            placeholder={`Es: "Fiorentina" = BISTECCA FIORENTINA BOVINO ADULTO · premi Invio per aggiungere`}
          />
          <Btn onClick={aggiungi} disabled={adding || !input.trim()} style={{ flexShrink: 0 }}>
            {adding ? "…" : "Aggiungi"}
          </Btn>
        </div>
      </Card>

      {/* Lista regole attive */}
      {attive.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase",
            letterSpacing: "0.5px", marginBottom: 10 }}>
            Attive ({attive.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {attive.map(r => (
              <div key={r.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                background: "white", border: `1px solid ${C.border}`,
                borderLeft: `4px solid ${C.primary}`,
                borderRadius: 10, padding: "12px 16px",
                boxShadow: C.shadow,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>✅</span>
                <div style={{ flex: 1, fontSize: 14, color: C.text, lineHeight: 1.5 }}>{r.testo}</div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => toggleAttiva(r.id)} title="Disabilita"
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                      cursor: "pointer", padding: "4px 8px", fontSize: 12, color: C.muted }}>
                    Pausa
                  </button>
                  <button onClick={() => elimina(r.id)} title="Elimina"
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                      cursor: "pointer", padding: "4px 8px", fontSize: 12, color: C.danger }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista regole in pausa */}
      {inattive.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase",
            letterSpacing: "0.5px", marginBottom: 10 }}>
            In pausa ({inattive.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {inattive.map(r => (
              <div key={r.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                background: C.bg, border: `1px solid ${C.border}`,
                borderLeft: `4px solid ${C.border}`,
                borderRadius: 10, padding: "12px 16px", opacity: 0.6,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>⏸</span>
                <div style={{ flex: 1, fontSize: 14, color: C.textSec, lineHeight: 1.5 }}>{r.testo}</div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={() => toggleAttiva(r.id)} title="Riattiva"
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                      cursor: "pointer", padding: "4px 8px", fontSize: 12, color: C.primary }}>
                    Riattiva
                  </button>
                  <button onClick={() => elimina(r.id)}
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
                      cursor: "pointer", padding: "4px 8px", fontSize: 12, color: C.danger }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {regole.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: C.muted, fontSize: 14 }}>
          Nessuna istruzione ancora.<br />
          <span style={{ fontSize: 13 }}>Aggiungi la prima regola qui sopra.</span>
        </div>
      )}

      <TrainingPanel />
    </div>
  );
}

// ─── Training Panel ───────────────────────────────────────────────────────────
function TrainingPanel() {
  const [esempi, setEsempi] = useState(null);

  useEffect(() => {
    axios.get("/api/ia/training/stats", authH())
      .then(({ data }) => setEsempi(data.esempi))
      .catch(() => {});
  }, []);

  function esporta() {
    const a = document.createElement("a");
    a.href = `/api/ia/training/export?token=${localStorage.getItem("token")}`;
    a.download = "training.jsonl";
    a.click();
  }

  const pct = esempi === null ? 0 : Math.min(100, Math.round((esempi / 50) * 100));
  const pronto = esempi >= 50;

  return (
    <Card style={{ padding: 20, marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Dataset di training</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
            Ogni proposta accettata diventa un esempio di training
          </div>
        </div>
        <Btn sm variant={pronto ? "wa" : "ghost"} onClick={esporta} disabled={!esempi}>
          ⬇ Esporta JSONL
        </Btn>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
        <div style={{ fontSize: 32, fontWeight: 700, color: pronto ? C.success : C.primary }}>
          {esempi ?? "…"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: C.textSec, marginBottom: 6 }}>
            {pronto
              ? "✅ Dataset pronto per il fine-tuning"
              : `${esempi ?? 0} / 50 esempi minimi per il fine-tuning`}
          </div>
          <div style={{ height: 8, background: C.border, borderRadius: 99, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: pronto ? C.success : C.primary,
              borderRadius: 99, transition: "width 0.4s ease" }} />
          </div>
        </div>
      </div>

      {pronto && (
        <div style={{ background: C.successLight, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: C.success, marginTop: 8 }}>
          Hai abbastanza esempi. Esporta il file JSONL e usalo con <strong>Unsloth</strong> sul laptop o con <strong>OpenAI fine-tuning API</strong>.
        </div>
      )}
    </Card>
  );
}

// ─── PropostaProdotti — tabella editabile con autocomplete ────────────────────
function PropostaProdotti({ prodotti, proposteId, codCliente, onUpdate }) {
  const [editIdx,      setEditIdx]      = useState(null);
  const [editQ,        setEditQ]        = useState("");
  const [editQAlt,     setEditQAlt]     = useState("");
  const [editSearch,   setEditSearch]   = useState("");
  const [editPrezzo,   setEditPrezzo]   = useState("");
  const [editAcqPrezzo, setEditAcqPrezzo] = useState("");
  const [prezziOpts,   setPrezziOpts]   = useState([]);
  const [prezziDisplay, setPrezziDisplay] = useState({});   // codice → prezzo ven display
  const [acquistoDisplay, setAcquistoDisplay] = useState({}); // codice → prezzo acq display
  const [inlinePrezzi, setInlinePrezzi] = useState({});     // idx → { ven, acq }
  const [suggs,        setSuggs]        = useState([]);
  const [showDrop,     setShowDrop]     = useState(false);
  const [editProd,     setEditProd]     = useState(null);
  const searchRef = useRef(null);
  const dropRef   = useRef(null);

  // Carica al volo prezzi vendita e acquisto mancanti per tutti i prodotti
  useEffect(() => {
    prodotti.forEach(async pr => {
      if (!pr.codice) return;
      if (!pr.selectedSalePrice && !prezziDisplay[pr.codice] && codCliente) {
        try {
          const { data } = await axios.get(
            `/api/prezzoven/${encodeURIComponent(pr.codice)}?codCliente=${encodeURIComponent(codCliente)}`,
            authH()
          );
          if (data?.length) {
            const val = parseFloat(String(data[0].PREZZO ?? "").replace(/[€\s]/g, "").replace(",", "."));
            if (!isNaN(val) && val > 0)
              setPrezziDisplay(prev => ({ ...prev, [pr.codice]: `${val.toFixed(2)} €` }));
          }
        } catch {}
      }
      if (!pr.purchasePrice && !acquistoDisplay[pr.codice]) {
        try {
          const { data } = await axios.get(`/api/prezzoacq/${encodeURIComponent(pr.codice)}`, authH());
          if (data?.price) setAcquistoDisplay(prev => ({ ...prev, [pr.codice]: data.price }));
        } catch {}
      }
    });
  }, [prodotti, codCliente]); // eslint-disable-line

  useEffect(() => {
    function outside(e) {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  async function caricaPrezzi(codice, currentVen, currentAcq) {
    setPrezziOpts([]);
    setEditAcqPrezzo(currentAcq || "");
    if (!codice) return;
    await Promise.all([
      codCliente
        ? axios.get(`/api/prezzoven/${encodeURIComponent(codice)}?codCliente=${encodeURIComponent(codCliente)}`, authH())
            .then(({ data }) => {
              const opzioni = (data || []).map(r => {
                const val = parseFloat(String(r.PREZZO ?? "").replace("€","").trim());
                return isNaN(val) || val <= 0 ? null : `${val.toFixed(2)} €`;
              }).filter(Boolean);
              setPrezziOpts(opzioni);
              if (opzioni.length > 0 && !currentVen) setEditPrezzo(opzioni[0]);
              else setEditPrezzo(currentVen || "");
            }).catch(() => { setPrezziOpts([]); setEditPrezzo(currentVen || ""); })
        : Promise.resolve(),
      axios.get(`/api/prezzoacq/${encodeURIComponent(codice)}`, authH())
        .then(({ data }) => { if (data?.price) setEditAcqPrezzo(data.price); })
        .catch(() => {}),
    ]);
  }

  async function startEdit(idx) {
    const p = prodotti[idx];
    setEditIdx(idx);
    setEditProd({ ...p });
    setEditQ(String(p.quantita || ""));
    setEditQAlt(p.qta_alternativa || "");
    setEditSearch(`${p.codice} ${p.descrizione}`);
    setSuggs([]);
    setShowDrop(false);
    setEditPrezzo(p.selectedSalePrice || "");
    await caricaPrezzi(p.codice, p.selectedSalePrice || "", p.purchasePrice || "");
    setTimeout(() => searchRef.current?.focus(), 50);
  }

  async function cercaProdotti(q) {
    if (!q.trim()) { setSuggs([]); setShowDrop(false); return; }
    try {
      const { data } = await axios.get(`/api/products?filter=${encodeURIComponent(q)}&IdForSearch=`, authH());
      const mapped = data.slice(0, 12).map(p => ({
        codice:      (p.code || p.productCode || "").trim(),
        descrizione: (p.productName || "").trim(),
        um:          (p.ARUNMIS1 || "").trim(),
      }));
      setSuggs(mapped);
      setShowDrop(mapped.length > 0);
    } catch { setSuggs([]); setShowDrop(false); }
  }

  const debouncedCerca = useCallback(
    (() => {
      let t;
      return (q) => { clearTimeout(t); t = setTimeout(() => cercaProdotti(q), 350); };
    })(), []
  );

  function selezionaSugg(s) {
    setEditProd(p => ({ ...p, codice: s.codice, descrizione: s.descrizione, um: s.um }));
    setEditSearch(`${s.codice} ${s.descrizione}`);
    setShowDrop(false);
    setSuggs([]);
    caricaPrezzi(s.codice, "", "");
  }

  async function salva() {
    const nuovaQta   = parseFloat(editQ) || editProd.quantita;
    const prezzoNum  = parseFloat((editPrezzo || "").replace("€","").trim()) || 0;
    const nuovi = prodotti.map((p, i) => {
      if (i !== editIdx) return p;
      const partial = nuovaQta > 0 && prezzoNum > 0
        ? (nuovaQta * prezzoNum).toFixed(2)
        : "";
      return {
        ...editProd,
        quantita:          nuovaQta,
        qta_alternativa:   editQAlt.trim(),
        selectedSalePrice: prezzoNum > 0 ? `${prezzoNum.toFixed(2)} €` : "",
        purchasePrice:     editAcqPrezzo.trim(),
        partialPrice:      partial,
      };
    });
    await axios.patch(`/api/ia/proposte/${proposteId}`, { prodotti: nuovi }, authH());
    onUpdate(nuovi);
    setEditIdx(null);
  }

  function parsePrezzo(raw) {
    return parseFloat(String(raw || "").replace(/[^\d.]/g, '')) || 0;
  }

  async function salvaPrezzInline(idx) {
    const patch = inlinePrezzi[idx];
    if (!patch) return;
    const nuovi = prodotti.map((p, i) => {
      if (i !== idx) return p;
      const venNum = patch.ven !== undefined ? parsePrezzo(patch.ven) : parsePrezzo(p.selectedSalePrice);
      const acqNum = patch.acq !== undefined ? parsePrezzo(patch.acq) : parsePrezzo(p.purchasePrice);
      const partial = p.quantita > 0 && venNum > 0 ? (p.quantita * venNum).toFixed(2) : "";
      return {
        ...p,
        selectedSalePrice: patch.ven !== undefined ? (venNum > 0 ? `${venNum.toFixed(2)} €` : "") : p.selectedSalePrice,
        purchasePrice:     patch.acq !== undefined ? (acqNum > 0 ? `${acqNum.toFixed(2)} €` : "") : p.purchasePrice,
        partialPrice:      partial,
      };
    });
    await axios.patch(`/api/ia/proposte/${proposteId}`, { prodotti: nuovi }, authH());
    onUpdate(nuovi);
    setInlinePrezzi(prev => { const n = { ...prev }; delete n[idx]; return n; });
  }

  async function elimina(idx) {
    const nuovi = prodotti.filter((_, i) => i !== idx);
    await axios.patch(`/api/ia/proposte/${proposteId}`, { prodotti: nuovi }, authH());
    onUpdate(nuovi);
    if (editIdx === idx) setEditIdx(null);
  }

  return (
    <Card style={{ marginBottom: 16, overflow: "visible" }}>
      <Table>
        <thead>
          <tr>
            <Th>Prodotto</Th>
            <Th style={{ width: 90 }}>QTA</Th>
            <Th style={{ width: 150 }}>QTA Alternativa</Th>
            <Th style={{ width: 110 }}>P. Acquisto</Th>
            <Th style={{ width: 110 }}>P. Vendita</Th>
            <Th style={{ width: 80 }}></Th>
          </tr>
        </thead>
        <tbody>
          {prodotti.map((p, i) => (
            <Tr key={i}>
              {editIdx === i ? (
                <>
                  <Td style={{ position: "relative" }}>
                    <Input
                      ref={searchRef}
                      value={editSearch}
                      onChange={e => { setEditSearch(e.target.value); debouncedCerca(e.target.value); }}
                      onFocus={() => suggs.length > 0 && setShowDrop(true)}
                      placeholder="Cerca per codice o descrizione…"
                      style={{ fontSize: 13 }}
                    />
                    {showDrop && (
                      <div ref={dropRef} style={{
                        position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0,
                        background: "white", border: `1px solid ${C.border}`, borderRadius: 8,
                        boxShadow: C.shadowLg, zIndex: 500, maxHeight: 220, overflowY: "auto",
                      }}>
                        {suggs.map((s, si) => (
                          <div key={si} onMouseDown={() => selezionaSugg(s)}
                            style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13,
                              borderBottom: `1px solid ${C.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background = C.bg}
                            onMouseLeave={e => e.currentTarget.style.background = "white"}>
                            <span style={{ fontFamily: "monospace", color: C.muted, marginRight: 8 }}>{s.codice}</span>
                            <strong>{s.descrizione}</strong>
                            <span style={{ color: C.muted, marginLeft: 6, fontSize: 11 }}>{s.um}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      value={editQ}
                      onChange={e => setEditQ(e.target.value)}
                      style={{ fontSize: 13, width: 80 }}
                    />
                  </Td>
                  <Td>
                    <Input
                      value={editQAlt}
                      onChange={e => setEditQAlt(e.target.value)}
                      placeholder="es. 2 casse"
                      style={{ fontSize: 13 }}
                    />
                  </Td>
                  <Td>
                    <Input
                      value={editAcqPrezzo}
                      onChange={e => setEditAcqPrezzo(e.target.value)}
                      placeholder="es. 3.20 €"
                      style={{ fontSize: 13 }}
                    />
                  </Td>
                  <Td>
                    {prezziOpts.length > 0 ? (
                      <select
                        value={editPrezzo}
                        onChange={e => setEditPrezzo(e.target.value)}
                        style={{ fontSize: 13, padding: "4px 6px", border: `1.5px solid ${C.border}`,
                          borderRadius: 6, fontFamily: "inherit", width: "100%", background: "white" }}
                      >
                        {prezziOpts.map((p, pi) => (
                          <option key={pi} value={p}>{p}{pi === 0 ? " (ultimo)" : ""}</option>
                        ))}
                        <option value="">— nessuno —</option>
                      </select>
                    ) : (
                      <Input
                        value={editPrezzo}
                        onChange={e => setEditPrezzo(e.target.value)}
                        placeholder="es. 5.50 €"
                        style={{ fontSize: 13 }}
                      />
                    )}
                  </Td>
                  <Td>
                    <BtnRow>
                      <Btn sm onClick={salva}>✓</Btn>
                      <Btn sm variant="ghost" onClick={() => setEditIdx(null)}>✕</Btn>
                    </BtnRow>
                  </Td>
                </>
              ) : (
                <>
                  <Td>
                    <span style={{ fontFamily: "monospace", fontSize: 12, color: C.muted, marginRight: 8 }}>
                      {p.codice}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{p.descrizione}</span>
                  </Td>
                  <Td style={{ fontWeight: 700, fontSize: 15 }}>
                    {p.quantita > 0
                      ? <>{p.quantita} <span style={{ fontSize: 12, color: C.muted, fontWeight: 400 }}>{p.um}</span></>
                      : <span style={{ color: C.muted, fontSize: 13 }}>—</span>}
                  </Td>
                  <Td>
                    {p.qta_alternativa
                      ? <Pill color={C.warnLight} textColor="#b45309" style={{ fontSize: 12 }}>{p.qta_alternativa}</Pill>
                      : <span style={{ color: C.muted, fontSize: 13 }}>—</span>}
                  </Td>
                  <Td style={{ fontSize: 13, fontWeight: 600 }}>
                    <input
                      value={inlinePrezzi[i]?.acq ?? String(p.purchasePrice || acquistoDisplay[p.codice] || "").replace(/\s*€\s*/g, "").trim()}
                      onChange={e => {
                        const v = e.target.value.replace(/[^\d.,]/g, '').replace(/,/g, '.');
                        setInlinePrezzi(prev => ({ ...prev, [i]: { ...prev[i], acq: v } }));
                      }}
                      onBlur={() => salvaPrezzInline(i)}
                      onKeyDown={e => e.key === 'Enter' && salvaPrezzInline(i)}
                      placeholder="n.d."
                      style={{ width: 68, textAlign: 'right', border: '1px solid #ccc', borderRadius: 3, padding: '2px 4px', fontSize: 13, color: '#7c3aed' }}
                    />
                  </Td>
                  <Td style={{ fontSize: 13, fontWeight: 600 }}>
                    <input
                      value={inlinePrezzi[i]?.ven ?? String(p.selectedSalePrice || prezziDisplay[p.codice] || "").replace(/\s*€\s*/g, "").trim()}
                      onChange={e => {
                        const v = e.target.value.replace(/[^\d.,]/g, '').replace(/,/g, '.');
                        setInlinePrezzi(prev => ({ ...prev, [i]: { ...prev[i], ven: v } }));
                      }}
                      onBlur={() => salvaPrezzInline(i)}
                      onKeyDown={e => e.key === 'Enter' && salvaPrezzInline(i)}
                      placeholder="n.d."
                      style={{ width: 68, textAlign: 'right', border: '1px solid #ccc', borderRadius: 3, padding: '2px 4px', fontSize: 13, color: '#15803d' }}
                    />
                  </Td>
                  <Td>
                    <BtnRow>
                      <Btn sm variant="ghost" onClick={() => startEdit(i)}>✏️</Btn>
                      <Btn sm variant="ghost" onClick={() => elimina(i)} style={{ color: C.danger }}>🗑</Btn>
                    </BtnRow>
                  </Td>
                </>
              )}
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

// ─── Modal: Prompt manuale ────────────────────────────────────────────────────
function PromptModal({ onClose, onSuccess }) {
  const [testo,          setTesto]          = useState("");
  const [dataOrdine,     setDataOrdine]     = useState("");
  const [clienteQuery,   setClienteQuery]   = useState("");
  const [clienteSel,     setClienteSel]     = useState(null); // { ANDCODICE, ANDESCRI }
  const [suggeriti,      setSuggeriti]      = useState([]);
  const [showList,       setShowList]       = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [stato,          setStato]          = useState(null);
  const [errMsg,         setErrMsg]         = useState("");
  const debounceRef = useRef(null);

  function onClienteInput(val) {
    setClienteQuery(val);
    setClienteSel(null);
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setSuggeriti([]); setShowList(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`/api/clienti?filter=${encodeURIComponent(val.trim())}`);
        setSuggeriti(data);
        setShowList(data.length > 0);
      } catch {}
    }, 350);
  }

  function seleziona(c) {
    setClienteSel(c);
    setClienteQuery(c.ANDESCRI);
    setShowList(false);
    setSuggeriti([]);
  }

  async function invia() {
    if (!testo.trim() || !dataOrdine || !clienteSel) return;
    setLoading(true);
    setStato(null);
    setErrMsg("");
    try {
      const { data } = await axios.post("/api/ia/analizza-prompt", {
        testo,
        cod_cliente_zucchetti:  clienteSel.ANCODICE,
        nome_cliente_zucchetti: clienteSel.ANDESCRI,
        data_ordine:            dataOrdine,
      }, authH());
      setStato("elaborazione");
      const poll = setInterval(async () => {
        try {
          const { data: p } = await axios.get(`/api/ia/proposte/${data.id}`, authH());
          if (p.stato !== "in_elaborazione") {
            clearInterval(poll);
            setLoading(false);
            if (p.stato === "errore") { setStato("errore"); setErrMsg(p.note_ai || "errore sconosciuto"); }
            else { setStato("ok"); setTimeout(() => { onSuccess(); onClose(); }, 1200); }
          }
        } catch { clearInterval(poll); setLoading(false); }
      }, 4000);
    } catch (err) {
      setLoading(false);
      setStato("errore");
      setErrMsg(err.response?.data?.error || err.message);
    }
  }

  const inputBase = { width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13,
    fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <ModalTitle>➕ Nuova proposta manuale</ModalTitle>

        {/* ── Cliente autocomplete */}
        <div style={{ marginBottom: 14, position: "relative" }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.textSec, display: "block", marginBottom: 6 }}>
            Cliente <span style={{ color: C.danger }}>*</span>
          </label>
          <input
            value={clienteQuery}
            onChange={e => onClienteInput(e.target.value)}
            onFocus={() => suggeriti.length > 0 && setShowList(true)}
            onBlur={() => setTimeout(() => setShowList(false), 150)}
            placeholder="Cerca per nome…"
            autoComplete="off"
            style={{ ...inputBase, border: `1.5px solid ${clienteSel ? C.success : C.border}` }}
          />
          {showList && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200,
              background: "white", border: `1px solid ${C.border}`, borderRadius: 8,
              boxShadow: C.shadowMd, maxHeight: 200, overflowY: "auto", marginTop: 2,
            }}>
              {suggeriti.map(c => (
                <div key={c.ANCODICE} onMouseDown={() => seleziona(c)}
                  style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13,
                    borderBottom: `1px solid ${C.border}`, display: "flex",
                    justifyContent: "space-between", alignItems: "center" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.primaryLight}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{ fontWeight: 500 }}>{c.ANDESCRI}</span>
                  <span style={{ color: C.muted, fontSize: 11 }}>{c.ANCODICE}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Data ordine */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.textSec, display: "block", marginBottom: 6 }}>
            Data ordine <span style={{ color: C.danger }}>*</span>
          </label>
          <input
            type="date"
            value={dataOrdine}
            onChange={e => setDataOrdine(e.target.value)}
            style={{ ...inputBase, border: `1.5px solid ${C.border}`, cursor: "pointer" }}
          />
        </div>

        {/* ── Testo ordine */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.textSec, display: "block", marginBottom: 6 }}>
            Testo ordine <span style={{ color: C.danger }}>*</span>
          </label>
          <textarea
            value={testo}
            onChange={e => setTesto(e.target.value)}
            placeholder={"es:\n50kg lombata\n30kg coscia di maiale\n2 casse petto di pollo"}
            rows={7}
            style={{ ...inputBase, border: `1.5px solid ${C.border}`, resize: "vertical" }}
          />
        </div>

        {stato === "elaborazione" && <div style={{ fontSize: 13, color: C.primary,   marginBottom: 10 }}>🤖 Analisi in corso…</div>}
        {stato === "ok"          && <div style={{ fontSize: 13, color: C.success,   marginBottom: 10 }}>✅ Proposta creata!</div>}
        {stato === "errore"      && <div style={{ fontSize: 13, color: C.danger,    marginBottom: 10 }}>❌ {errMsg}</div>}

        <BtnRow style={{ justifyContent: "flex-end" }}>
          <Btn sm variant="ghost" onClick={onClose} disabled={loading}>Annulla</Btn>
          <Btn sm onClick={invia} disabled={loading || !testo.trim() || !dataOrdine || !clienteSel}>
            {loading ? "🤖 Analisi…" : "Analizza"}
          </Btn>
        </BtnRow>
      </Modal>
    </Overlay>
  );
}

// ─── Tab: Proposte ────────────────────────────────────────────────────────────
function TabProposte() {
  const [proposte, setProposte] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [subTab,   setSubTab]   = useState("attesa"); // "attesa" | "accettate"
  const [showPrompt, setShowPrompt] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/ia/proposte", authH());
      setProposte(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  async function loadDetail(id) {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/ia/proposte/${id}`, authH());
      setSelected(data);
    } catch {}
    finally { setLoading(false); }
  }

  async function aggiornaStato(id, stato) {
    const { data } = await axios.patch(`/api/ia/proposte/${id}`, { stato }, authH());
    load();
    if (selected?.id === id)
      setSelected(s => ({ ...s, stato, ordine_id: data.ordine_id ?? s.ordine_id }));
  }

  async function elimina(id) {
    if (!window.confirm("Eliminare questa proposta?")) return;
    await axios.delete(`/api/ia/proposte/${id}`, authH());
    if (selected?.id === id) setSelected(null);
    load();
  }

  const statoStyle = (s) => ({
    in_elaborazione: { bg: "#eff6ff", text: "#3b82f6", label: "🤖 Elaborazione…" },
    pending:         { bg: C.warnLight,    text: "#d97706", label: "⏳ In attesa" },
    accettata:       { bg: C.successLight, text: C.success,  label: "✅ Accettata" },
    rifiutata:       { bg: C.dangerLight,  text: C.danger,   label: "❌ Rifiutata" },
    errore:          { bg: C.dangerLight,  text: C.danger,   label: "⚠️ Errore" },
  }[s] || {});

  const certStyle = (c) => ({
    alta:  { bg: C.successLight, text: C.success },
    media: { bg: C.warnLight,    text: "#d97706"  },
    bassa: { bg: C.dangerLight,  text: C.danger   },
  }[c] || {});

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <Btn sm onClick={() => setShowPrompt(true)}>➕ Aggiungi</Btn>
      </div>
      <div style={{ display: "flex", gap: 16, height: "calc(100vh - 320px)", minHeight: 420 }}>
      {/* Lista sinistra */}
      {(() => {
        const listaAttesa    = proposte.filter(p => p.stato !== "accettata").slice(0, 50);
        const listaAccettate = proposte.filter(p => p.stato === "accettata").slice(0, 50);
        const lista = subTab === "attesa" ? listaAttesa : listaAccettate;
        return (
          <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", border: `1px solid ${C.border}`, borderRadius: 12, background: "white", overflow: "hidden" }}>
            {/* Sotto-tab header */}
            <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              {[
                { key: "attesa",    label: "In attesa",  count: listaAttesa.length    },
                { key: "accettate", label: "Accettate",  count: listaAccettate.length },
              ].map(t => (
                <button key={t.key} onClick={() => setSubTab(t.key)} style={{
                  flex: 1, padding: "10px 8px", border: "none", background: "none", cursor: "pointer",
                  fontFamily: "inherit", fontSize: 12, fontWeight: 600,
                  color: subTab === t.key ? C.primary : C.textSec,
                  borderBottom: subTab === t.key ? `2px solid ${C.primary}` : "2px solid transparent",
                  marginBottom: -1,
                }}>
                  {t.label} <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: subTab === t.key ? C.primaryLight : C.bg,
                    color: subTab === t.key ? C.primary : C.muted,
                    borderRadius: 10, minWidth: 18, height: 18, fontSize: 11, padding: "0 5px", marginLeft: 4,
                  }}>{t.count}</span>
                </button>
              ))}
            </div>
            {/* Lista */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {lista.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>
                  {subTab === "attesa"
                    ? <>Nessuna proposta in attesa.<br />Vai in WhatsApp e clicca<br /><strong>Proponi ordine</strong>.</>
                    : "Nessuna proposta accettata."}
                </div>
              )}
              {lista.map(p => {
                const ss = statoStyle(p.stato);
                const cs = certStyle(p.certezza);
                return (
                  <div key={p.id} onClick={() => loadDetail(p.id)} style={{
                    padding: "12px 16px", borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                    background: selected?.id === p.id ? C.primaryLight : "transparent",
                    borderLeft: `3px solid ${selected?.id === p.id ? C.primary : "transparent"}`,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{p.client_nome}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                      {p.n_prodotti} prodotti · {fmtTs(p.created_at)}
                    </div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      <Pill color={ss.bg} textColor={ss.text} style={{ fontSize: 11 }}>{ss.label}</Pill>
                      <Pill color={cs.bg} textColor={cs.text} style={{ fontSize: 11 }}>{p.certezza}</Pill>
                      {p.data_ordine && (
                        <Pill color={C.warnLight} textColor="#b45309" style={{ fontSize: 11 }}>
                          📅 {fmtDate(p.data_ordine)}
                        </Pill>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Dettaglio destra */}
      <div style={{ flex: 1, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 12, background: "white" }}>
        {loading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted }}>
            Caricamento…
          </div>
        )}
        {!loading && !selected && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted, gap: 8 }}>
            <span style={{ fontSize: 36 }}>📋</span>
            <span style={{ fontSize: 14 }}>Seleziona una proposta</span>
          </div>
        )}
        {!loading && selected && (
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>Cliente: {selected.client_nome}</div>
                {selected.cod_cliente_zucchetti
                  ? <Pill color={C.successLight} textColor={C.success} style={{ fontSize: 11, marginTop: 4 }}>
                      {selected.cod_cliente_zucchetti} — {selected.nome_cliente_zucchetti}
                    </Pill>
                  : <span style={{ fontSize: 11, color: C.muted, marginTop: 4, display: "block" }}>
                      ⚠️ Cliente non trovato in Zucchetti
                    </span>
                }
                {selected.ordine_id && (
                  <div style={{ marginTop: 6, fontSize: 12, color: C.success, fontWeight: 600 }}>
                    ✅ Ordine creato: {selected.ordine_id}
                  </div>
                )}
                <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{fmtTs(selected.created_at)}</div>
              </div>
              <BtnRow>
                {selected.stato === "pending" && <>
                  <Btn sm variant="ghost" onClick={() => aggiornaStato(selected.id, "rifiutata")} style={{ color: C.danger, borderColor: C.border }}>
                    ❌ Rifiuta
                  </Btn>
                  <Btn sm onClick={() => aggiornaStato(selected.id, "accettata")}>
                    ✅ Accetta
                  </Btn>
                </>}
                <Btn sm variant="ghost" onClick={() => elimina(selected.id)} style={{ color: C.danger, borderColor: C.border }}>🗑</Btn>
              </BtnRow>
            </div>

            {/* Data ordine */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.textSec, whiteSpace: "nowrap" }}>📅 Data ordine:</span>
              <input
                type="date"
                value={selected.data_ordine ? String(selected.data_ordine).slice(0, 10) : ""}
                onChange={async e => {
                  const val = e.target.value || null;
                  await axios.patch(`/api/ia/proposte/${selected.id}`, { data_ordine: val }, authH());
                  setSelected(s => ({ ...s, data_ordine: val }));
                }}
                style={{
                  padding: "5px 10px", border: `1.5px solid ${C.border}`, borderRadius: 8,
                  fontSize: 13, fontFamily: "inherit", color: selected.data_ordine ? C.text : C.muted,
                  background: selected.data_ordine ? C.warnLight : "white", cursor: "pointer",
                }}
              />
              {selected.data_ordine && (
                <button
                  onClick={async () => {
                    await axios.patch(`/api/ia/proposte/${selected.id}`, { data_ordine: null }, authH());
                    setSelected(s => ({ ...s, data_ordine: null }));
                  }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 16, padding: "0 2px" }}
                  title="Rimuovi data"
                >✕</button>
              )}
            </div>

            {selected.note_ai && (
              <div style={{ background: C.primaryLight, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: C.primary, marginBottom: 16 }}>
                💬 {selected.note_ai}
              </div>
            )}

            <PropostaProdotti
              prodotti={selected.prodotti || []}
              proposteId={selected.id}
              codCliente={selected.cod_cliente_zucchetti}
              onUpdate={nuovi => setSelected(s => ({ ...s, prodotti: nuovi }))}
            />

            <details>
              <summary style={{ fontSize: 12, color: C.muted, cursor: "pointer", userSelect: "none" }}>
                Mostra conversazione analizzata
              </summary>
              <div style={{ marginTop: 8, background: C.bg, borderRadius: 8, padding: 12,
                fontSize: 12, color: C.textSec, whiteSpace: "pre-wrap", fontFamily: "monospace",
                maxHeight: 280, overflowY: "auto", border: `1px solid ${C.border}` }}>
                {selected.chat_snapshot}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
    {showPrompt && (
      <PromptModal
        onClose={() => setShowPrompt(false)}
        onSuccess={load}
      />
    )}
    </>
  );
}

// ─── Tab: Persi ───────────────────────────────────────────────────────────────
function TabPersi({ clienti }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get("/api/crm/persi", authH());
    setItems(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function del(id) {
    if (!window.confirm("Eliminare questa trattativa?")) return;
    await axios.delete(`/api/crm/persi/${id}`, authH());
    load();
  }

  const perMotivo = MOTIVI.map(m => ({
    motivo: m,
    count: items.filter(i => i.motivo === m).length,
  })).filter(x => x.count > 0);

  return (
    <div>
      <TopBar>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Trattative perse</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{items.length} totali</div>
        </div>
        <Btn onClick={() => setModal(true)}>+ Aggiungi</Btn>
      </TopBar>

      {perMotivo.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
          {perMotivo.map(x => (
            <div key={x.motivo} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 16px", fontSize: 13, boxShadow: C.shadow }}>
              <span style={{ fontWeight: 700, color: C.primary, fontSize: 18 }}>{x.count}</span>
              <span style={{ color: C.textSec, marginLeft: 6 }}>{x.motivo}</span>
            </div>
          ))}
        </div>
      )}

      <Card>
        {items.length === 0 ? (
          <EmptyState>Nessuna trattativa persa registrata</EmptyState>
        ) : (
          <>
            <DesktopOnly>
              <div style={{ overflowX: "auto" }}>
                <Table>
                  <thead>
                    <tr>
                      <Th>Nome contatto</Th>
                      <Th>Telefono</Th>
                      <Th>Motivo</Th>
                      <Th>Note</Th>
                      <Th>Data</Th>
                      <Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(p => (
                      <Tr key={p.id}>
                        <Td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Avatar name={p.nome_contatto} size={30} />
                            <span style={{ fontWeight: 600 }}>{p.nome_contatto || "—"}</span>
                          </div>
                        </Td>
                        <Td style={{ color: C.muted, fontSize: 13 }}>{p.telefono || "—"}</Td>
                        <Td>
                          {p.motivo
                            ? <Pill color={C.warnLight} textColor="#b45309">{p.motivo}</Pill>
                            : <span style={{ color: C.muted }}>—</span>}
                        </Td>
                        <Td style={{ color: C.muted, fontSize: 13 }}>{p.note || "—"}</Td>
                        <Td style={{ color: C.textSec, fontSize: 13 }}>{fmtDate(p.data)}</Td>
                        <Td>
                          <Btn sm variant="ghost" onClick={() => del(p.id)} style={{ color: C.danger }}>🗑</Btn>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </DesktopOnly>
            <MobileOnly>
              {items.map(p => (
                <div key={p.id} style={{
                  padding: "14px 16px",
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                }}>
                  <Avatar name={p.nome_contatto} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{p.nome_contatto || "—"}</div>
                    {p.telefono && <div style={{ fontSize: 13, color: C.muted, marginTop: 1 }}>{p.telefono}</div>}
                    <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {p.motivo && <Pill color={C.warnLight} textColor="#b45309">{p.motivo}</Pill>}
                      <span style={{ fontSize: 12, color: C.muted }}>{fmtDate(p.data)}</span>
                    </div>
                    {p.note && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{p.note}</div>}
                  </div>
                  <Btn sm variant="ghost" onClick={() => del(p.id)} style={{ color: C.danger, flexShrink: 0 }}>🗑</Btn>
                </div>
              ))}
            </MobileOnly>
          </>
        )}
      </Card>

      {modal && (
        <PersoModal
          clienti={clienti}
          onClose={() => setModal(false)}
          onSave={() => { setModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Notifica Banner ──────────────────────────────────────────────────────────
function NotificaBanner({ notifiche, onClose, onNavigate }) {
  if (!notifiche.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 3000,
      display: "flex", flexDirection: "column", gap: 10, pointerEvents: "none" }}>
      {notifiche.map(n => (
        <NotifCard key={n.id} $tipo={n.tipo} onClick={() => onNavigate(n.proposteId)}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>{n.tipo === "success" ? "✅" : "⚠️"}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{n.titolo}</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{n.sottotitolo}</div>
          </div>
          <button onClick={e => { e.stopPropagation(); onClose(n.id); }}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)",
              cursor: "pointer", fontSize: 18, padding: "0 4px", flexShrink: 0 }}>✕</button>
        </NotifCard>
      ))}
    </div>
  );
}

// ─── Main CRM ─────────────────────────────────────────────────────────────────
export default function CRM() {
  const [activeTab, setActiveTab]       = useState("clienti");
  const [clienti, setClienti]           = useState([]);
  const [nonLetti, setNonLetti]         = useState(0);
  const [nPropostePending, setNProposte] = useState(0);
  const [notifiche, setNotifiche]        = useState([]);
  const prevInElab    = useRef(new Set());
  const initialized   = useRef(false);

  const loadClienti = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/crm/clienti", authH());
      setClienti(data);
    } catch {}
  }, []);

  const loadNonLetti = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/crm/whatsapp-conversazioni", authH());
      setNonLetti(data.reduce((s, c) => s + Number(c.non_letti || 0), 0));
    } catch {}
  }, []);

  const addNotifica = useCallback((notifica) => {
    const id = Date.now() + Math.random();
    setNotifiche(n => [...n, { ...notifica, id }]);
    setTimeout(() => setNotifiche(n => n.filter(x => x.id !== id)), 8000);
  }, []);

  const loadPropostePending = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/ia/proposte", authH());
      setNProposte(data.filter(p => p.stato === "pending").length);

      const currentInElab = new Set(
        data.filter(p => p.stato === "in_elaborazione").map(p => p.id)
      );

      if (initialized.current) {
        prevInElab.current.forEach(id => {
          if (!currentInElab.has(id)) {
            const p = data.find(x => x.id === id);
            if (!p) return;
            const dataFmt = fmtTs(p.created_at);
            if (p.stato === "pending") {
              addNotifica({
                tipo: "success",
                titolo: `Proposta per ${p.client_nome} pronta`,
                sottotitolo: dataFmt,
                proposteId: p.id,
              });
            } else if (p.stato === "errore") {
              addNotifica({
                tipo: "error",
                titolo: `Analisi per ${p.client_nome} fallita`,
                sottotitolo: p.note_ai?.slice(0, 60) || dataFmt,
                proposteId: p.id,
              });
            }
          }
        });
      }

      prevInElab.current = currentInElab;
      initialized.current = true;
    } catch {}
  }, [addNotifica]);

  useEffect(() => {
    loadClienti();
    loadNonLetti();
    loadPropostePending();
    const iv = setInterval(() => { loadNonLetti(); loadPropostePending(); }, 10000);
    return () => clearInterval(iv);
  }, [loadClienti, loadNonLetti, loadPropostePending]);

  const followupScaduti = clienti.filter(c =>
    c.stato === "follow_up" || (c.prossimo_followup && new Date(c.prossimo_followup) <= new Date())
  ).length;

  function navigaAProposta(proposteId) {
    setActiveTab("proposte");
    setNotifiche(n => n.filter(x => x.proposteId !== proposteId));
  }

  return (
    <>
      <GlobalFonts />
      <NotificaBanner
        notifiche={notifiche}
        onClose={id => setNotifiche(n => n.filter(x => x.id !== id))}
        onNavigate={navigaAProposta}
      />
      <Wrap>
        <PageHeader>
          <PageTitle>
            <span style={{ fontSize: 24 }}>📋</span> CRM
          </PageTitle>
          <div style={{ fontSize: 13, color: C.muted }}>
            {clienti.length} clienti · {followupScaduti} follow-up
          </div>
        </PageHeader>

        <TabBar>
          <Tab active={activeTab === "clienti"} onClick={() => setActiveTab("clienti")}>
            👥 Clienti
            {clienti.length > 0 && <Badge variant="accent">{clienti.length}</Badge>}
          </Tab>
          <Tab active={activeTab === "followup"} onClick={() => setActiveTab("followup")}>
            📅 Follow-up
            {followupScaduti > 0 && <Badge>{followupScaduti}</Badge>}
          </Tab>
          <Tab active={activeTab === "whatsapp"} onClick={() => setActiveTab("whatsapp")}>
            <WhatsAppIcon size={14} color={activeTab === "whatsapp" ? C.primary : C.muted} />
            WhatsApp
            {nonLetti > 0 && <Badge>{nonLetti}</Badge>}
          </Tab>
          <Tab active={activeTab === "persi"} onClick={() => setActiveTab("persi")}>
            😔 Persi
          </Tab>
          <Tab active={activeTab === "proposte"} onClick={() => setActiveTab("proposte")}>
            🤖 Proposte
            {nPropostePending > 0 && <Badge>{nPropostePending}</Badge>}
          </Tab>
          <Tab active={activeTab === "addestra"} onClick={() => setActiveTab("addestra")}>
            🧠 Addestra
          </Tab>
        </TabBar>

        {activeTab === "clienti"  && <TabClienti clienti={clienti} reload={loadClienti} />}
        {activeTab === "followup" && <TabFollowup />}
        {activeTab === "whatsapp" && (
          <TabWhatsapp
            clientiRefresh={loadClienti}
            onPropostaCreata={() => { loadPropostePending(); setActiveTab("proposte"); }}
          />
        )}
        {activeTab === "persi"    && <TabPersi clienti={clienti} />}
        {activeTab === "proposte" && <TabProposte />}
        {activeTab === "addestra" && <TabAddestra />}
      </Wrap>
    </>
  );
}
